import { groupRxImages, pickLatestDigitalRx } from '../../utils/rxEntry';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import useVisitStore from '../../stores/visitStore';
import { usePermission, useScope } from '../../context/PermissionContext';
import { useTheme } from '../../theme/ThemeContext';
import VisitActionsPanel from './VisitActionsPanel';
import PatientHistory from './PatientHistory';
import AddPrescription from './AddPrescription';
import DigitalPrescriptionForm from './DigitalPrescriptionForm';
import PrescriptionReadOnlyView from './PrescriptionReadOnlyView';
import { isFollowUpSet } from './FollowUpPicker';
import SmartSuggestionBar from './SmartSuggestionBar';
import { useMetadataNotifications } from '../../hooks/useMetadataNotifications';
import { PAYMENT_MODES } from '../../constants/visitMetadata';
import { downloadFileSecure } from '../../services/api/visitMetadataApi';
import { openPrescriptionPdf } from '../../services/api/prescriptionPdfApi';
import { downloadVisitsExcel } from '../../services/api/visitsExportApi';
import useFileViewerStore from '../../stores/useFileViewerStore';
import useWorkflowStore, { WORKFLOW_ACTIONS } from '../../stores/workflowStore';
import syncEngine from '../../services/SyncEngine';
import { fetchVisitMetadata } from '../../services/api/visitMetadataApi';
import { PhoneButton } from '../common/PhoneButton';
import VisitSearchInput from './VisitSearchInput';
import { filterVisitsBySearch, VISIT_SEARCH_COPY } from '../../utils/visitSearch';
import { fetchClinicBranding, fetchVisitById } from '../../services/api/visitApi';
import SendVisitDialog from './SendVisitDialog';
import IncomingReferralBanner from './IncomingReferralBanner';
import { formatDoseSchedule } from '../../utils/rxDosage';
import VisitJourney from './VisitJourney';
import ToothFindingsSummary from './ToothFindingsSummary';
import VisitReceiptsModal from './VisitReceiptsModal';
import DailyCollectionSummary from './DailyCollectionSummary';
import useChartType from '../../charts/core/useChartType';
import { useWidgetSDK } from '../../widgets';
import useResumeVisit from '../../hooks/useResumeVisit';
import ResumeConflictDialog from '../dashboard/ResumeConflictDialog';
import { fetchRefundFlags } from './receiptEligibilityFlags';
import useVisitCompletionGuard from '../../hooks/useVisitCompletionGuard';
import useOwnDoctorSpecialization from '../../hooks/useOwnDoctorSpecialization';
import { fetchStaffList } from '../../services/api/staffApi';
import useAssignableProviders, { DOCTORS_LOAD_ERROR_COPY } from '../../hooks/useAssignableProviders';
import ScopeCaption, { useDeskWallViewer } from '../common/ScopeCaption';
import { STAFF_ROLES } from '../../constants/staff';
import {
  PROVIDER_CATEGORIES,
  providerCategoryOf,
  providerCategory,
  visitMatchesAssignee,
  VISIT_TYPE_COLORS,
  VISIT_TYPE_LABELS,
} from '../../constants/visitProviderCategories';
import useDepartmentStore from '../../stores/departmentStore';
import DeptFilter from '../admission/board/DeptFilter';
import { filterProvidersByDept } from './departmentResolution';
// TOKEN-UPDATE / T2 — frozen FE-A/FE-B contracts (t2-build-plan.md §FE-internal
// contracts). These files ship on parallel branches (feat/token-update-t2-helpers,
// feat/token-update-t2-print-sheet) — binding to the FROZEN signatures, not to
// built code. Until those branches merge, this import is unresolved on THIS
// branch alone (expected; see task DoD).
import { visitMatchesDeptFilter } from '../../utils/visitMatchesDeptFilter';
import { hasPrescriptionContent } from '../../utils/prescriptionContent';
import { SURGERY_SECTION_LABEL } from '../../utils/rxMemory';
import { printQueueSheet, printThermalQueueSheet } from '../../utils/printQueueSheet';
import { fetchPrintSummary } from '../../services/api/visitsExportApi';
import { formatDateIST } from '../../utils/formatIST';
import { formatRupees } from '../../utils/visitMoney';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ManageVisitPage — Themed, responsive visit list with filter bar
 *
 * Backend: GET /api/organisation/visits?date=&status=&doctor_id=
 *          PUT /api/organisation/visits/:id  { status }
 *
 * Statuses: QUEUE, IN_PROGRESS, COMPLETED, CANCELLED
 * ═══════════════════════════════════════════════════════════════════════
 */

const STATUS_COLORS = {
  QUEUE:       { bg: 'rgba(59,130,246,0.12)',  text: '#3b82f6' },
  IN_PROGRESS: { bg: 'rgba(13, 148, 136,0.12)',  text: '#0d9488' },
  COMPLETED:   { bg: 'rgba(34,197,94,0.12)',   text: '#22c55e' },
  CANCELLED:   { bg: 'rgba(107,114,128,0.12)', text: '#6b7280' },
};

// Org/Admin view: individual status filters
const STATUS_FILTERS_ORG = [
  { value: '', label: 'All' },
  { value: 'QUEUE', label: 'Queue' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// Doctor (scoped) view: merged QUEUE+IN_PROGRESS as "Active"
const STATUS_FILTERS_DOCTOR = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active', match: ['QUEUE', 'IN_PROGRESS'] },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// Provider-type filter chips (primary row). "All" first, then each provider
// category from the single-source config — so a new provider type appears here
// automatically. `shortLabel` (falling back to `label`) keeps the chip
// space-constrained (e.g. "OTC" not the full "Outpatient Treatment Center
// (OTC)"). The status chips above become the sub-filter beneath this row.
const PROVIDER_FILTERS = [
  { key: '', label: 'All' },
  ...PROVIDER_CATEGORIES.map((c) => ({ key: c.key, label: c.shortLabel || c.label, icon: c.icon })),
];

// VISIT_TYPE_COLORS now lives in constants/visitProviderCategories.js (imported)
// so the badge colors stay single-sourced with the provider config.

// Roster helpers (shared with RegisterVisitForm's member display conventions).
const memberIdOf = (m) => m.member_id || m.id;
const roleOf = (m) => String(m.role || '').toUpperCase();
const memberName = (m) =>
  `${(m.first_name || '').trim()} ${(m.last_name || '').trim()}`.trim()
  || m.full_name || m.name || m.email || 'Staff';

// Label for the assignee dropdown's "all" option, per selected provider.
const ASSIGNEE_ALL_LABEL = {
  '': 'All Staff',
  OPD: 'All Doctors',
  NURSE: 'All OTC Staff',
  LAB: 'All Lab Technicians',
};

function todayISO() {
  // Return today's date in IST (matches backend IST storage)
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

// Return the IST date `n` days before today as "YYYY-MM-DD".
// Anchors on todayISO() (IST-correct) then does pure date arithmetic at
// UTC-midnight so it never crosses an IST/UTC day boundary.
function daysAgoISO(n) {
  const d = new Date(`${todayISO()}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

/**
 * Format visit_time for display.
 * Backend returns IST time as "HH:MM:SS" (e.g. "14:35:00") or full ISO string.
 * Returns "2:35 PM" style string.
 */
function formatVisitTime(timeVal) {
  if (!timeVal) return '';
  try {
    // Handle bare "HH:MM:SS" or "HH:MM" — already in IST from backend
    const bareMatch = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(timeVal);
    if (bareMatch) {
      const h = parseInt(bareMatch[1], 10);
      const m = parseInt(bareMatch[2], 10);
      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    }
    // Full ISO timestamp — use Intl
    const d = new Date(timeVal);
    if (isNaN(d)) return '';
    return d.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

// S8 (EP-4) — settlement_date -> "dd/mm/yyyy" (stories.md S8 AC3 literal
// spec). Accepts an ISO "YYYY-MM-DD" (or any Date-parseable string);
// missing/invalid input degrades to '' so a chip never prints "Invalid Date".
function formatSettlementDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr.length <= 10 ? `${dateStr}T00:00:00` : dateStr);
  if (isNaN(d)) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/* ──────────────────────────────────────────────────────────────
   ProviderTag — single source for the "who is attending" line on a
   visit row. OTC visits (visit_type === 'NURSE') show the nurse with
   a teal ⚕ OTC pill; all other visits keep the "🩺 Dr. {name}" render.
   Used by both the main card and the compact/mobile row so they can't drift.
   ────────────────────────────────────────────────────────────── */
function ProviderTag({ visit, isDark }) {
  if (visit.visit_type === 'NURSE') {
    // OTC (W3 XOR) is attended by EXACTLY ONE of a nurse OR a doctor. Show the
    // actual attendee — the nurse (⚕ Name) or, for a doctor-attended OTC visit,
    // the doctor (🩺 Dr. Name) — always under the OTC pill (category, not
    // attendee, drives the pill/section).
    const attendee = visit.nurse_name
      ? { icon: '⚕', name: visit.nurse_name }
      : (visit.doctor_name ? { icon: '🩺', name: `Dr. ${visit.doctor_name}` } : null);
    if (!attendee) return null;
    return (
      <span style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', display: 'inline-flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>{attendee.icon} {attendee.name}</span>
        <span style={{
          fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
          padding: '0.0625rem 0.375rem', borderRadius: '9999px',
          background: isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.12)',
          color: isDark ? '#5eead4' : '#0d9488',
          border: `1px solid ${isDark ? 'rgba(20,184,166,0.4)' : 'rgba(13,148,136,0.35)'}`,
        }}>{providerCategory('NURSE')?.shortLabel || 'OTC'}</span>
      </span>
    );
  }
  if (visit.visit_type === 'LAB') {
    if (!visit.lab_technician_name) return null;
    return (
      <span style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', display: 'inline-flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>🧪 {visit.lab_technician_name}</span>
        <span style={{
          fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
          padding: '0.0625rem 0.375rem', borderRadius: '9999px',
          background: isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.12)',
          color: isDark ? '#c4b5fd' : '#7c3aed',
          border: `1px solid ${isDark ? 'rgba(139,92,246,0.4)' : 'rgba(124,58,237,0.35)'}`,
        }}>{VISIT_TYPE_LABELS.LAB}</span>
      </span>
    );
  }
  if (!visit.doctor_name) return null;
  return (
    <span style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
      🩺 Dr. {visit.doctor_name}
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────
   PrescriptionPanel — Inline panel below a visit card
   Dual tabs: Add Prescription (digital) + Upload Prescription (file)
   ────────────────────────────────────────────────────────────── */
function UploadPrescriptionPanel({ visit, isDark, onSuccess, onClose, userRole }) {
  // Doctors always get the prescription pad only: no upload tab, and the
  // remembered tab preference is never consulted for them.
  const isDoctor = (userRole || '').toUpperCase() === STAFF_ROLES.DOCTOR;
  // Use backend-stored preference as default tab
  const { prescriptionTabPreference, prescriptionTabPreferenceFetched, fetchPreferences, recordPrescriptionTabUsage } = useWorkflowStore();
  // Same specialised symptom list the doctor's own accordion (VisitActionsPanel)
  // shows — fall back to the logged-in doctor's own specialization when the
  // visit carries none, instead of silently reading as COMMON-only.
  const ownSpecialization = useOwnDoctorSpecialization();
  const doctorSpecialization = visit.doctor_specialization || ownSpecialization;
  const [subTab, setSubTab] = React.useState(isDoctor ? 'add' : prescriptionTabPreference);
  const [prescriptions, setPrescriptions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [currentRxMetaId, setCurrentRxMetaId] = React.useState(null);
  // Just-generated Rx data → passed as initialData so a post-generate remount
  // re-syncs (incl. outside investigations) instead of re-initialising to [].
  const [currentRxData, setCurrentRxData] = React.useState(null);

  // Fetch preference once on mount
  useEffect(() => { fetchPreferences(); }, [fetchPreferences]);

  // Sync subTab once backend preference is actually fetched
  const prefSynced = useRef(false);
  useEffect(() => {
    if (!isDoctor && prescriptionTabPreferenceFetched && !prefSynced.current) {
      setSubTab(prescriptionTabPreference);
      prefSynced.current = true;
    }
  }, [prescriptionTabPreference, prescriptionTabPreferenceFetched, isDoctor]);

  // FIX: Use SyncEngine for deduplicated, cached metadata fetch
  const loadPrescriptions = useCallback(async () => {
    try {
      setLoading(true);
      const result = await syncEngine.fetchMetadata(
        visit.visit_id,
        async (vid) => fetchVisitMetadata(vid),
        { source: 'UploadPrescriptionPanel' }
      );
      const data = result?.data;
      setPrescriptions(groupRxImages(data?.entries || []));
    } catch {
      setPrescriptions([]);
    } finally {
      setLoading(false);
    }
  }, [visit.visit_id]);

  useEffect(() => {
    loadPrescriptions();
  }, [loadPrescriptions]);

  // Auto-resume the latest digital Rx into the pad (mirrors VisitActionsPanel):
  // seeds on first load and re-syncs the SAME meta_id when fresher data (with
  // attached_images) lands post-save. Never overwrites a save targeting another id.
  const latestDigitalRx = pickLatestDigitalRx(prescriptions);
  const latestRxImageIds = (latestDigitalRx?.attached_images || []).map((img) => img.meta_id).join(',');
  // COMPLETED visit + saved Rx -> read-only view with an explicit Edit step
  // (same as VisitActionsPanel); the edit then versions the saved Rx.
  const isCompletedWithRx = visit.status === 'COMPLETED' && !!latestDigitalRx;
  const [editingCompletedRx, setEditingCompletedRx] = React.useState(false);
  useEffect(() => {
    if (isCompletedWithRx || !latestDigitalRx) return;
    if (!currentRxMetaId || currentRxMetaId === latestDigitalRx.meta_id) {
      setCurrentRxMetaId(latestDigitalRx.meta_id);
      setCurrentRxData(latestDigitalRx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestDigitalRx?.meta_id, latestRxImageIds, isCompletedWithRx, currentRxMetaId]);

  const handleUploadSuccess = (tab) => {
    syncEngine.invalidateMetadata(visit.visit_id);
    loadPrescriptions();
    onSuccess?.();
    if (tab) recordPrescriptionTabUsage(tab);
  };

  const handleViewFile = async (entry) => {
    try {
      const { blobUrl, contentType } = await downloadFileSecure(visit.visit_id, entry.meta_type, entry.meta_id);
      const ft = contentType?.startsWith('image') ? 'image' : 'pdf';
      useFileViewerStore.getState().openViewer({
        blobUrl,
        fileType: ft,
        title: entry.meta_type === 'PRESCRIPTION' ? 'Prescription' : entry.meta_type === 'REPORT' ? 'Report' : 'File',
      });
    } catch (err) {
      alert('Failed to open file: ' + (err?.message || 'Unknown error'));
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const subTabs = [
    { key: 'add', label: '💊 Add Prescription' },
    ...(isDoctor ? [] : [{ key: 'upload', label: '📤 Upload Prescription' }]),
  ];

  // The "Saved (n)" list — ONE derivation feeding both the count and the rows,
  // so the header can never claim entries the body doesn't render (bk-1165).
  // A digital entry belongs here only if it has content by the ONE shared rule
  // (`utils/prescriptionContent`); a contentless one is dropped rather than
  // falling through to the file branch, which would draw a paper row with no
  // file behind it. File-backed entries are untouched.
  const savedEntries = prescriptions.filter(
    (e) => e.prescription_type !== 'digital' || hasPrescriptionContent(e),
  );

  return (
    <div
      style={{
        margin: '0.5rem 0 0',
        borderRadius: '0.75rem',
        border: `1px solid ${isDark ? 'rgba(13, 148, 136,0.2)' : 'rgba(13, 148, 136,0.15)'}`,
        background: isDark ? 'rgba(13, 148, 136,0.04)' : 'rgba(13, 148, 136,0.02)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.625rem 1rem 0',
        }}
      >
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: isDark ? '#2dd4bf' : '#0f766e' }}>
          💊 Prescription
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: isDark ? '#6b7280' : '#9ca3af',
            cursor: 'pointer',
            fontSize: '1rem',
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>

      {/* Sub-tab switcher */}
      <div style={{
        display: 'flex', gap: '0.125rem',
        padding: '0.375rem 1rem 0',
        borderBottom: `1px solid ${isDark ? 'rgba(13, 148, 136,0.1)' : 'rgba(13, 148, 136,0.06)'}`,
      }}>
        {subTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            style={{
              padding: '0.375rem 0.625rem',
              borderRadius: '0.375rem 0.375rem 0 0',
              border: 'none',
              borderBottom: subTab === t.key
                ? `2px solid ${isDark ? '#5eead4' : '#0f766e'}`
                : '2px solid transparent',
              background: subTab === t.key
                ? (isDark ? 'rgba(13, 148, 136,0.1)' : 'rgba(13, 148, 136,0.06)')
                : 'transparent',
              fontSize: '0.6875rem',
              fontWeight: subTab === t.key ? 600 : 400,
              color: subTab === t.key
                ? (isDark ? '#5eead4' : '#0f766e')
                : (isDark ? '#9ca3af' : '#6b7280'),
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '0.5rem 1rem' }}>
        {subTab === 'add' && (isCompletedWithRx && !editingCompletedRx ? (
          <PrescriptionReadOnlyView
            prescriptionEntry={latestDigitalRx}
            visitId={visit.visit_id}
            onEdit={() => setEditingCompletedRx(true)}
            onGeneratePdf={() => openPrescriptionPdf(visit.visit_id)}
          />
        ) : (
          <DigitalPrescriptionForm
            visit={visit}
            visitId={visit.visit_id}
            doctorSpecialization={doctorSpecialization}
            onSuccess={(result) => {
              if (result?.metaId) setCurrentRxMetaId(result.metaId);
              if (result?.data) setCurrentRxData(result.data);
              handleUploadSuccess('add');
              // A failed image upload keeps the editor open (error + staged file stay for retry).
              if (!result?.imageUploadFailed) setEditingCompletedRx(false);
            }}
            patientId={visit.patient_id}
            editMetaId={editingCompletedRx ? latestDigitalRx?.meta_id : (currentRxMetaId || undefined)}
            initialData={editingCompletedRx ? latestDigitalRx : (currentRxData || undefined)}
            visitStatus={visit.status}
            metadataLoaded={!loading}
          />
        ))}

        {!isDoctor && subTab === 'upload' && (
          <AddPrescription
            visitId={visit.visit_id}
            metaType="PRESCRIPTION"
            onSuccess={() => handleUploadSuccess('upload')}
            onClose={onClose}
          />
        )}
      </div>

      {/* Already-saved prescriptions */}
      {!loading && savedEntries.length > 0 && (
        <div
          style={{
            padding: '0.5rem 1rem 0.75rem',
            borderTop: `1px solid ${isDark ? 'rgba(13, 148, 136,0.08)' : 'rgba(13, 148, 136,0.06)'}`,
          }}
        >
          <div
            style={{
              fontSize: '0.625rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: isDark ? '#6b7280' : '#9ca3af',
              marginBottom: '0.375rem',
            }}
          >
            💊 Saved ({savedEntries.length})
          </div>
          {savedEntries.map((entry) => {
            // Digital prescription. The branch is the ENTRY TYPE, never
            // `entry.medicines` (bk-1165): `[]` is truthy so a lab-only Rx used
            // to render "Digital — 0 medicines", and `null` is falsy so a
            // legacy Rx used to fall into the file branch below and draw a
            // phantom "File" row with no file behind it. `savedEntries` has
            // already dropped contentless digital entries.
            if (entry.prescription_type === 'digital') {
              const medicines = entry.medicines || [];
              const investigations = entry.investigations || [];
              const advisedSurgeries = entry.advised_surgeries || [];
              const diagnosis = (entry.diagnosis || '').trim();
              return (
                <div
                  key={entry.meta_id || entry.entry_id}
                  style={{
                    padding: '0.375rem 0.5rem',
                    marginBottom: '0.25rem',
                    borderRadius: '0.5rem',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  }}
                >
                  {/* Header self-gates: a count is printed only when there is
                      a real one to print. "0 medicines" would be a fabricated
                      zero on a lab-only or diagnosis-only Rx. */}
                  <div style={{ fontSize: '0.5625rem', color: isDark ? '#6b7280' : '#9ca3af', marginBottom: '0.25rem' }}>
                    {medicines.length > 0
                      ? `💊 Digital — ${medicines.length} medicine${medicines.length !== 1 ? 's' : ''}`
                      : '💊 Digital'}
                  </div>
                  {/* Diagnosis — content in its own right, and on a
                      medicine-less Rx often the only thing to show. Same line
                      PatientHistory renders. */}
                  {diagnosis && (
                    <div style={{
                      fontSize: '0.625rem',
                      color: isDark ? '#e8e9ed' : '#1f2937',
                      marginBottom: '0.25rem',
                    }}>
                      <span style={{ fontWeight: 700, color: isDark ? '#9ca3af' : '#6b7280' }}>Diagnosis: </span>
                      {diagnosis}
                    </div>
                  )}
                  {medicines.map((med, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap',
                      fontSize: '0.6875rem', color: isDark ? '#e8e9ed' : '#374151',
                      marginBottom: '0.125rem',
                    }}>
                      <span style={{ fontWeight: 600 }}>{med.medicine}</span>
                      {med.strength && <span style={{ color: '#0d9488', fontSize: '0.625rem' }}>{med.strength}</span>}
                      {med.dosage_schedule && <span style={{ color: '#3b82f6', fontSize: '0.625rem' }}>
                        {formatDoseSchedule(med.dosage_schedule)}
                      </span>}
                      {med.timing && <span style={{ color: isDark ? '#6b7280' : '#9ca3af', fontSize: '0.5625rem' }}>{med.timing}</span>}
                      {med.duration && <span style={{ color: isDark ? '#6b7280' : '#9ca3af', fontSize: '0.5625rem' }}>{med.duration}</span>}
                    </div>
                  ))}
                  {investigations.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center', marginTop: '0.25rem' }}>
                      <span style={{ fontSize: '0.5625rem', fontWeight: 600, color: isDark ? '#fbbf24' : '#d97706' }}>🧪</span>
                      {investigations.map((inv, i) => (
                        <span key={i} style={{
                          fontSize: '0.5rem', padding: '0.0625rem 0.3rem', borderRadius: '0.25rem',
                          background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.08)',
                          color: isDark ? '#fbbf24' : '#d97706', fontWeight: 500,
                        }}>{inv}</span>
                      ))}
                    </div>
                  )}
                  {/* Advised surgeries — `hasPrescriptionContent` already counts
                      them, so a surgeries-only entry reaches this list; without
                      this block it rendered as "💊 Digital" with nothing in it.
                      Same treatment (and the same shared heading) as
                      PatientHistory / PrescriptionReadOnlyView; null/[] renders
                      nothing at all. */}
                  {advisedSurgeries.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center', marginTop: '0.25rem' }}>
                      <span style={{ fontSize: '0.5625rem', fontWeight: 600, color: isDark ? '#c4b5fd' : '#5b21b6' }}>
                        {`🔪 ${SURGERY_SECTION_LABEL}`}
                      </span>
                      {advisedSurgeries.map((s, i) => (
                        <span key={i} style={{
                          fontSize: '0.5rem', padding: '0.0625rem 0.3rem', borderRadius: '0.25rem',
                          background: isDark ? 'rgba(124,58,237,0.2)' : '#ede9fe',
                          color: isDark ? '#c4b5fd' : '#5b21b6', fontWeight: 500,
                        }}>{s}</span>
                      ))}
                    </div>
                  )}
                  {/* Follow-up is single-sourced on the visit (patient-name label),
                      not per-prescription. */}
                </div>
              );
            }

            // File-based prescription
            const isImage = entry.content_type?.startsWith('image/');
            return (
              <div
                key={entry.meta_id || entry.entry_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  padding: '0.375rem 0.5rem',
                  marginBottom: '0.25rem',
                  borderRadius: '0.5rem',
                  background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '0.875rem', flexShrink: 0 }}>{isImage ? '🖼️' : '📄'}</span>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '0.6875rem',
                        fontWeight: 500,
                        color: isDark ? '#e8e9ed' : '#374151',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {entry.file_name || 'File'}
                    </div>
                    {entry.file_size && (
                      <span style={{ fontSize: '0.5625rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
                        {formatSize(entry.file_size)}
                      </span>
                    )}
                  </div>
                </div>
                {entry.file_name && (
                  <button
                    onClick={() => handleViewFile(entry)}
                    style={{
                      padding: '0.2rem 0.5rem',
                      borderRadius: '0.375rem',
                      border: `1px solid ${isDark ? 'rgba(13, 148, 136,0.3)' : 'rgba(13, 148, 136,0.2)'}`,
                      background: isDark ? 'rgba(13, 148, 136,0.1)' : 'rgba(13, 148, 136,0.05)',
                      color: '#0d9488',
                      fontSize: '0.5625rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    👁 View
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function ManageVisitPage({ onBack, initialFocusVisitId }) {
  const canManageQueue = usePermission('MANAGE_QUEUE');
  const canCancelVisit = usePermission('CANCEL_VISIT');
  const canApproveVisit = usePermission('APPROVE_VISIT');
  const canCompleteVisit = usePermission('COMPLETE_VISIT');
  const canReferVisit = usePermission('REFER_VISIT');
  const canChangeDate = usePermission('CHANGE_DATE');
  const canUploadPrescription = usePermission('UPLOAD_PRESCRIPTION');
  const canGeneratePdf = usePermission('GENERATE_PRESCRIPTION_PDF');
  const canViewHistory = usePermission('VIEW_PATIENT_HISTORY');
  const canExport = usePermission('EXPORT_VISIT');
  const canPrintToken = usePermission('PRINT_TOKEN');
  const canExecuteSitting = usePermission('EXECUTE_SITTING');
  const { isScoped, scopeLabel, role: userRole, memberId, isAdmin } = useScope();
  const deskWalled = useDeskWallViewer();

  // Date-picker access:
  //   ADMIN / DOCTOR (CHANGE_DATE) — unrestricted.
  //   RECEPTIONIST    — enabled but bounded to the last 7 days (today..−7d).
  const isReceptionist = (userRole || '').toUpperCase() === 'RECEPTIONIST';
  const canPickDate = canChangeDate || isReceptionist;
  const dateMin = isReceptionist && !canChangeDate ? daysAgoISO(7) : undefined;
  const dateMax = isReceptionist && !canChangeDate ? todayISO() : undefined;
  const { requestResume, dialogProps: resumeDialogProps } = useResumeVisit({
    viewerMemberId: memberId,
  });
  const { isDark } = useTheme();
  const isDentist = useChartType() === 'dental';
  const sdk = useWidgetSDK();

  const {
    visits, loading, error, successMessage,
    fetchVisits, updateVisit, clearError, clearSuccess,
  } = useVisitStore();

  // Staff roster (doctors + nurses + lab techs) for the provider-aware assignee
  // dropdown. Cached single-flight fetch (5-min TTL) — see staffApi.fetchStaffList.
  const [staff, setStaff] = useState([]);

  // ── Expanded visit panel ─────────────────────────────
  const [expandedVisitId, setExpandedVisitId] = useState(null);
  const visitListRef = useRef(null);

  // ── Receipts / refund modal — Phase 12.5 ─────────────
  // { visit, mode: 'receipts' | 'refund' } | null
  const [receiptsModalVisit, setReceiptsModalVisit] = useState(null);
  const canRefundPayment = usePermission('REFUND_PAYMENT');

  // ── Daily collection summary — Phase 12.6 ────────────
  const canSeeDailySummary = usePermission('VIEW_DAILY_COLLECTION_SUMMARY');

  // Phase 1f — single-visit fallback. When the viewer lands on
  // /org#visit=X but the store's filtered list doesn't contain X (junior
  // / nurse assignee on a senior's visit), we fetch it independently and
  // merge it into the visible list.
  const [injectedVisit, setInjectedVisit] = useState(null);

  // ── Patient History modal ────────────────────────────
  const [historyPatient, setHistoryPatient] = useState(null); // { patient_id, patient_name, mobile }
  // D44/S19 — the DEBOUNCED search term. The box owns the keystrokes; this is
  // only what the list is actually filtered by.
  const [searchTerm, setSearchTerm] = useState('');

  // ── Upload Prescription panel (inline per visit) ─────
  const [uploadPrescriptionVisitId, setUploadPrescriptionVisitId] = useState(null);

  // ── Prescription PDF generation ──────────────────────
  const [pdfGenerating, setPdfGenerating] = useState(null); // visit_id being generated
  const [pdfError, setPdfError] = useState(null); // { visitId, message }

  // ── Export Excel ─────────────────────────────────────
  const [exporting, setExporting] = useState(null); // 'standard' | 'busy' | null
  const [exportError, setExportError] = useState(null);

  // ── Print Queue (S2/S3/S4, +T4 format chooser) — one smart button, gated
  // EXPORT_VISIT ─────────────────────────────────────────────────────────
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState(null);
  // Single-flight guard (RC10) — a double-click/rapid re-click can't fire a
  // second fetchPrintSummary while one is in flight, regardless of which
  // format (A4/thermal) triggered it.
  const printInFlightRef = useRef(false);
  // T4 split-button menu (A4 / Thermal 80mm) — closes on outside click.
  const [printMenuOpen, setPrintMenuOpen] = useState(false);
  const printMenuRef = useRef(null);
  useEffect(() => {
    if (!printMenuOpen) return undefined;
    const handleOutsideClick = (e) => {
      if (printMenuRef.current && !printMenuRef.current.contains(e.target)) setPrintMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [printMenuOpen]);

  const handleGeneratePdf = useCallback(async (visitId) => {
    setPdfGenerating(visitId);
    setPdfError(null);
    try {
      await openPrescriptionPdf(visitId);
    } catch (err) {
      setPdfError({ visitId, message: err.message || 'Failed to generate PDF' });
      setTimeout(() => setPdfError(null), 5000);
    } finally {
      setPdfGenerating(null);
    }
  }, []);

  // ── Metadata notification badges ─────────────────────
  const { getUnseenCount, hasDetails, markSeen, markHasDetails } = useMetadataNotifications(userRole);

  // When user expands a visit → mark it as seen
  const handleToggleDetails = useCallback((visitId) => {
    setExpandedVisitId((prev) => {
      if (prev === visitId) return null; // closing
      markSeen(visitId); // opening — clear unseen counter
      return visitId;
    });
  }, [markSeen]);

  // ── Filter config (scope-aware) ──────────────────────
  const statusFilters = isScoped ? STATUS_FILTERS_DOCTOR : STATUS_FILTERS_ORG;

  // ── Filter state ─────────────────────────────────────
  const [filters, setFilters] = useState({
    provider: '',  // '' = All providers; else OPD | NURSE | LAB (provider key)
    status: '',  // Default to All — active workflow is on homepage
    assignee_id: '',  // selected person's member_id (doctor / nurse / lab tech)
    department_id: '',  // '' = All departments (S1)
    // S8 — Lab Visit tab lens (EP-4 query params). '' = All. Only surfaced
    // in LAB scope (see showLabFilters below); reset whenever the provider
    // filter leaves LAB so a stale IPD/settlement pick can't silently keep
    // narrowing an OPD/Nurse export or fetch.
    type: '',        // '' | 'IPD' | 'OPD'
    settlement: '',  // '' | 'SETTLED' | 'UNSETTLED'
    date: todayISO(),
  });

  // Lab Visit tab lens is relevant for the unscoped LAB provider filter AND
  // for a scoped lab technician's own queue (stories.md Screen D actor line
  // — "lab tech ... see IPD entries"); dept/assignee stay hidden for scoped
  // users (self-view), but Type/Settlement narrow their own mixed IPD+OPD
  // queue just as usefully.
  const showLabFilters = filters.provider === 'LAB'
    || (isScoped && (userRole || '').toUpperCase() === STAFF_ROLES.LAB_TECHNICIAN);

  // ── Clinic branding for token print (single fetch on mount) ──
  const [branding, setBranding] = useState({ name: 'Clinic', description: '', address: '', pincode: '', phone: '' });
  useEffect(() => {
    fetchClinicBranding().then(setBranding).catch(() => {});
  }, []);

  // ── Department filter (S1) — single-flight org dept list, reused from the
  // A2 board component (DeptFilter). Hidden entirely when the org has 0 active
  // departments (S1.3) or for scoped DOCTOR (self-only, no provider/dept picker).
  const departments = useDepartmentStore((s) => s.departments);
  const deptsLoaded = useDepartmentStore((s) => s.deptsLoaded);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);
  const hydrateAllMemberDepts = useDepartmentStore((s) => s.hydrateAllMemberDepts);

  // Single fetch on mount — guarded by deptsLoaded so a re-render never re-fires.
  useEffect(() => { if (!deptsLoaded) fetchDepartments(); }, [deptsLoaded, fetchDepartments]);
  // Member→dept edges for the reverse assignee filter below. hydrateAllMemberDepts
  // is itself single-flight/guarded (departmentStore), so this is safe to call
  // once departments exist without storming (one fetch per dept, not per member).
  useEffect(() => {
    if (departments.length > 0) hydrateAllMemberDepts?.();
  }, [departments.length, hydrateAllMemberDepts]);

  const deptFilterOptions = useMemo(
    () => (departments || [])
      .filter((d) => d && d.active !== false)
      .map((d) => ({ id: d.dept_id || d.id, name: d.name })),
    [departments],
  );

  // F2 — dept_id → name lookup for the Receipts modal's reprinted token
  // "Department" row. Deliberately UNFILTERED by `active` (unlike
  // deptFilterOptions above) — a visit can be stamped with a department
  // that's since been deactivated, and the reprint should still show its
  // name. Single source (departmentStore, already loaded above); no fetch.
  const deptNameById = useMemo(() => {
    const m = {};
    for (const d of (departments || [])) m[d.dept_id || d.id] = d.name;
    return m;
  }, [departments]);

  // S10-AC2 — the DOCTOR options of the assignee dropdown come from the
  // server-scoped assignable list (the viewer's departments + no-department
  // doctors; never the org roster, not even while it loads). Nurse / lab-tech
  // options (S10-AC5) and every NAME lookup below (`staff`) stay org-wide.
  // Scoped viewers don't see the dropdown — role null = idle, no request.
  const { providers: assignableDoctors, error: assignableDoctorsError } = useAssignableProviders(isScoped ? null : STAFF_ROLES.DOCTOR);

  // Assignee dropdown options — scoped to the selected provider's role, or all
  // three clinical roles when "All" is selected (combined cross-role list), THEN
  // reverse-filtered to the selected department (A2 pattern — filterProvidersByDept;
  // unassigned/org-wide staff always stay visible under any specific dept).
  const assigneeOptions = useMemo(() => {
    const cat = providerCategory(filters.provider);
    // A category may list MORE than its primary staffRole (OTC = nurse OR doctor,
    // W3 XOR) — iterate its full `assigneeRoles` set so a doctor-attended OTC
    // visit is filterable by its attending doctor. Falls back to the single role.
    const roles = cat
      ? (cat.assigneeRoles || [cat.staffRole])
      : [STAFF_ROLES.DOCTOR, STAFF_ROLES.NURSE, STAFF_ROLES.LAB_TECHNICIAN];
    const roleScoped = [
      ...(roles.includes(STAFF_ROLES.DOCTOR) ? assignableDoctors : []),
      ...(staff || []).filter((m) => roleOf(m) !== STAFF_ROLES.DOCTOR && roles.includes(roleOf(m))),
    ].sort((a, b) => memberName(a).localeCompare(memberName(b)));
    return filterProvidersByDept(roleScoped, filters.department_id, deptsByMember);
  }, [staff, assignableDoctors, filters.provider, filters.department_id, deptsByMember]);

  // Selecting a department (S1 screen interactions) — Department persists across
  // provider changes; the currently-selected assignee clears ONLY if they fall
  // outside the newly-chosen department (fail-open on un-hydrated / no-dept staff,
  // same filterProvidersByDept carve-out).
  const handleDeptFilterChange = useCallback((deptId) => {
    setFilters((f) => {
      if (!f.assignee_id) return { ...f, department_id: deptId };
      const currentMember = (staff || []).filter((m) => memberIdOf(m) === f.assignee_id);
      const stillValid = filterProvidersByDept(currentMember, deptId, deptsByMember).length > 0;
      return { ...f, department_id: deptId, assignee_id: stillValid ? f.assignee_id : '' };
    });
  }, [staff, deptsByMember]);

  // Payments narrow per-person ONLY for doctors (OPD) — the ledger attributes by
  // doctor, not nurse/lab. A selected nurse/lab narrows the visit list, not the
  // payment cards (provider-level there).
  const paymentsDoctorId = useMemo(() => {
    if (!filters.assignee_id) return null;
    if (filters.provider === 'NURSE') return null;
    const sel = (staff || []).find((m) => memberIdOf(m) === filters.assignee_id);
    return sel && roleOf(sel) === STAFF_ROLES.DOCTOR ? filters.assignee_id : null;
  }, [staff, filters.assignee_id, filters.provider]);

  const handleExport = useCallback(async (format) => {
    setExporting(format);
    setExportError(null);
    try {
      // Export groups by doctor, so only pass a doctor assignee (OPD).
      // S8 (EP-5) — Export inherits the active Type/Settlement lens (both
      // '' outside LAB scope, since the provider switch resets them).
      await downloadVisitsExcel(filters.date, format, paymentsDoctorId || undefined, {
        type: filters.type || undefined,
        settlement: filters.settlement || undefined,
      });
    } catch (err) {
      setExportError(err.message || 'Export failed');
      setTimeout(() => setExportError(null), 5000);
    } finally {
      setExporting(null);
    }
  }, [filters.date, filters.type, filters.settlement, paymentsDoctorId]);

  // ── Load staff roster for the assignee dropdown (skip for scoped users) ──
  useEffect(() => {
    if (isScoped) return undefined;
    let cancelled = false;
    fetchStaffList()
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r) ? r : (r?.data || r?.staff || r?.members || []);
        setStaff(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isScoped]);

  // ── Fetch visits whenever filters change ─────────────
  const loadVisits = useCallback(() => {
    const params = {};
    // For compound filters (ACTIVE = QUEUE+IN_PROGRESS), don't send status
    // to backend — fetch all and filter client-side
    if (filters.status && filters.status !== 'ACTIVE') {
      params.status = filters.status;
    }
    // Provider + assignee + department are applied client-side (see
    // providerScopedVisits/filteredVisits) so the cross-role person filter and
    // the dept filter (S1) work uniformly with ZERO extra requests; the server
    // fetch stays by date. Depend on the PRIMITIVE fields actually sent (not the
    // whole `filters` object) so switching provider/assignee/department never
    // re-fires this fetch — only a status or date change does (RC5/RC6).
    if (filters.date) params.date = filters.date;
    // S8 (EP-4) — Lab Visit tab Type/Settlement lens. Unlike provider/
    // assignee/department (client-filtered, see providerScopedVisits below),
    // these narrow which IPD-lab rows the BACKEND synthesizes/returns, so
    // they MUST be a real query param and MUST re-fetch on change.
    if (filters.type) params.type = filters.type;
    if (filters.settlement) params.settlement = filters.settlement;
    // Phase 10 — doctors get visits they were forwarded out of, too,
    // so the original consultation stays on their My Visits as a
    // "Past consultations" entry. No effect for non-doctor roles.
    if ((userRole || '').toUpperCase() === 'DOCTOR') {
      params.includeForwarded = true;
    }
    fetchVisits(params);
  }, [filters.status, filters.date, filters.type, filters.settlement, fetchVisits, userRole]);

  useEffect(() => { loadVisits(); }, [loadVisits]);

  // ── Client-side filtering ────────────────────────────
  // Phase 1f — merge store visits with the single-visit fallback (if any)
  // so assignees who landed via `#visit=X` see that visit even when it
  // wasn't in the filtered list. De-dupe by visit_id; store row wins.
  const mergedVisits = useMemo(() => {
    const base = visits || [];
    if (!injectedVisit) return base;
    if (base.some((v) => v.visit_id === injectedVisit.visit_id)) return base;
    return [injectedVisit, ...base];
  }, [visits, injectedVisit]);

  // Provider + assignee + department scope — applied first so the status
  // sub-filter, header count, payments, print sheet, and emergency banner all
  // operate within the chosen scope. Reuses the single-source helpers from
  // visitProviderCategories + visitMatchesDeptFilter (S1). GATE 3 RULING
  // (owner, 2026-07-30 — reversal of the earlier RC5 carve-out): a specific
  // department STRICTLY excludes unassigned/EMERGENCY-sentinel visits; only
  // "All" (no department selected) shows them.
  const providerScopedVisits = useMemo(() => {
    let out = mergedVisits || [];
    if (filters.provider) out = out.filter((v) => providerCategoryOf(v) === filters.provider);
    if (filters.assignee_id) out = out.filter((v) => visitMatchesAssignee(v, filters.assignee_id));
    out = out.filter((v) => visitMatchesDeptFilter(v, filters.department_id));
    return out;
  }, [mergedVisits, filters.provider, filters.assignee_id, filters.department_id]);

  // Ensures WS status changes immediately remove visits from filtered view
  const statusScopedVisits = useMemo(() => {
    if (!filters.status) return providerScopedVisits; // "All" status — show everything in scope

    // Find matching statuses for the selected filter
    const filterDef = statusFilters.find((sf) => sf.value === filters.status);
    const matchStatuses = filterDef?.match || [filters.status];

    return (providerScopedVisits || []).filter((v) => matchStatuses.includes(v.status));
  }, [providerScopedVisits, filters.status, statusFilters]);

  // D44/S19 — patient search, applied LAST so it narrows WITHIN the provider /
  // assignee / department / status scope above, and so every downstream reader
  // (the rows, the print sheet, the header counts, the `#visit=` focus) sees
  // exactly the rows the doctor is looking at. Client-side over the loaded
  // page: no fetch, no new param, and no visibility the viewer did not have.
  const filteredVisits = useMemo(
    () => filterVisitsBySearch(statusScopedVisits, searchTerm),
    [statusScopedVisits, searchTerm],
  );
  // The count line's denominator is the FILTERED set (32 in "Showing 4 of 32
  // visits"), not the whole day — the other filters are still doing their job.
  const searchActive = searchTerm.trim() !== '';
  const otherFiltersActive = !!(filters.provider || filters.assignee_id
    || filters.department_id || filters.status);

  // S8/C1 — Lab Visit tab: split rows into IPD-lab then OPD-lab sections
  // (mirrors the print sheet's IPD/OPD split, FE-1) using the single-source
  // `is_ipd_lab` discriminator — never re-derived. Only relevant while the
  // Lab Visit tab is the active provider filter; every other tab keeps the
  // flat `filteredVisits.map` unchanged (`labVisitRows` stays null so the
  // render below falls back to the flat list, header-less, exactly as
  // before). Each item carries `idx` local to its own section so the
  // existing "no top border on the first row" rule still applies per
  // section rather than across the whole list.
  const labVisitRows = useMemo(() => {
    if (filters.provider !== 'LAB') return null;
    const ipdRows = filteredVisits.filter((v) => v.is_ipd_lab);
    const opdRows = filteredVisits.filter((v) => !v.is_ipd_lab);
    const out = [];
    if (ipdRows.length > 0) {
      out.push({ type: 'header', key: 'lab-section-ipd', testId: 'lab-section-IPD', title: 'IPD' });
      ipdRows.forEach((visit, idx) => out.push({ type: 'visit', visit, idx }));
    }
    if (opdRows.length > 0) {
      out.push({ type: 'header', key: 'lab-section-opd', testId: 'lab-section-OPD', title: 'OPD' });
      opdRows.forEach((visit, idx) => out.push({ type: 'visit', visit, idx }));
    }
    return out;
  }, [filteredVisits, filters.provider]);

  const visitRows = labVisitRows
    || filteredVisits.map((visit, idx) => ({ type: 'visit', visit, idx }));

  // ── Print Queue (S2/S3/S4, +T4 format chooser) ────────
  // WYSIWYG: the sheet prints EXACTLY `filteredVisits` — the same rows on
  // screen (dept + provider + assignee + status-chip scope already applied
  // above). ONE fetchPrintSummary per click (single-flight guard), no refetch
  // on filter switches. Scoped DOCTOR gets the button too — the server
  // self-scopes the summary map to their own visits, no doctor picker here.
  // `format` ('a4' | 'thermal', T4) picks the renderer only — the gathered
  // rows/summaryMap/filterBits are shared by both; each renderer computes
  // its own Day Summary footer from rows × summaryMap (Issue-2).
  const handlePrintQueue = useCallback(async (format = 'a4') => {
    if (printInFlightRef.current) return;
    printInFlightRef.current = true;
    setPrinting(true);
    setPrintError(null);
    try {
      const result = await fetchPrintSummary(filters.date);
      const summaryMap = result?.visits || {};
      const rows = [...filteredVisits].sort(
        (a, b) => (Number(a.queue_number) || 0) - (Number(b.queue_number) || 0),
      );
      const isLab = filters.provider === 'LAB';
      // Issue-3: every provider filter prints SECTIONED — "All" mixes
      // independent queues (each doctor's OPD queue, Nurse, Lab); the OPD
      // filter spans every doctor's own queue, the OTC filter ('NURSE' key)
      // every attendee's (F12/F8), and the Lab & Diagnostic filter now splits
      // IPD / OPD sections like its on-screen list (OR-55; the R1 print split
      // builds on this). A single-queue scope (e.g. one doctor's OPD) is
      // flattened by groupVisitsForPrint's <=1-section collapse, so no
      // renderer change.
      const sectioned = true;

      const filterBits = [];
      const providerCat = providerCategory(filters.provider);
      if (providerCat) filterBits.push(providerCat.label);
      if (filters.assignee_id) {
        const sel = (staff || []).find((m) => memberIdOf(m) === filters.assignee_id);
        if (sel) filterBits.push(memberName(sel));
      }
      const deptOpt = deptFilterOptions.find((d) => d.id === filters.department_id);
      if (deptOpt) filterBits.push(deptOpt.name);
      if (filters.status) {
        const sf = statusFilters.find((s) => s.value === filters.status);
        if (sf) filterBits.push(sf.label);
      }

      const dateLabel = formatDateIST(filters.date) || filters.date;

      // Day Summary (Collected/Refunded/Net) is computed INSIDE the renderers
      // from `rows` × `summaryMap` — Σ over the printed rows only, so it ties
      // to the Net Paid column under any client-side filter (Issue-2
      // architect contract check). Never the response-level `day_totals`
      // (that covers the caller's whole server-scoped day, not this
      // filtered subset).
      if (format === 'thermal') {
        printThermalQueueSheet({
          orgName: branding.name,
          filterLine: filterBits.join(' · '),
          dateLabel,
          rows,
          summaryMap,
          sectioned,
        });
      } else {
        printQueueSheet({
          title: `${branding.name} — ${isLab ? 'Lab & Diagnostic queue sheet' : 'Queue sheet'}`,
          subtitle: filterBits.length ? `${dateLabel} · ${filterBits.join(' · ')}` : dateLabel,
          provider: isLab ? 'LAB' : 'OPD',
          rows,
          summaryMap,
          sectioned,
        });
      }
    } catch (err) {
      setPrintError(err?.message || 'Failed to print queue');
      setTimeout(() => setPrintError(null), 5000);
    } finally {
      setPrinting(false);
      printInFlightRef.current = false;
    }
  }, [
    filters.date, filters.provider, filters.assignee_id, filters.department_id, filters.status,
    filteredVisits, staff, deptFilterOptions, statusFilters, branding.name,
  ]);

  // ── Active visit for SmartSuggestionBar (one bar, not per-visit) ──
  // Pick the first IN_PROGRESS visit, else the first QUEUE visit.
  const activeVisit = useMemo(() => {
    const inProgress = filteredVisits.find((v) => v.status === 'IN_PROGRESS');
    if (inProgress) return inProgress;
    return filteredVisits.find((v) => v.status === 'QUEUE') || null;
  }, [filteredVisits]);

  // ── Auto-focus visit from dashboard navigation ───────
  useEffect(() => {
    if (!initialFocusVisitId || !filteredVisits?.length) return;
    // Only focus if the target visit is actually in the list now
    // (important for the single-visit fallback case where the injected
    // row arrives asynchronously).
    if (!filteredVisits.some((v) => v.visit_id === initialFocusVisitId)) return;
    // Auto-expand the target visit
    setExpandedVisitId(initialFocusVisitId);
    // Scroll to the visit card after a tick (DOM needs to render)
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-visit-id="${initialFocusVisitId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(timer);
  }, [initialFocusVisitId, filteredVisits]);

  // Phase 1f — if the focused visit isn't in the store's filtered list
  // (junior / nurse assignee case), fetch it via GET /visits/{id} which
  // is gated by `can_view_visit` and returns 404 for strangers.
  useEffect(() => {
    if (!initialFocusVisitId) return undefined;
    const inStore = (visits || []).some(
      (v) => v.visit_id === initialFocusVisitId,
    );
    if (inStore) return undefined;
    if (injectedVisit && injectedVisit.visit_id === initialFocusVisitId) return undefined;
    let cancelled = false;
    (async () => {
      const row = await fetchVisitById(initialFocusVisitId);
      if (!cancelled && row) setInjectedVisit(row);
    })();
    return () => { cancelled = true; };
  }, [initialFocusVisitId, visits, injectedVisit]);

  useEffect(() => {
    if (successMessage) { const t = setTimeout(clearSuccess, 3000); return () => clearTimeout(t); }
  }, [successMessage, clearSuccess]);

  // ── Emergency visit detection ───────────────────────
  const [emergencyDismissed, setEmergencyDismissed] = useState(false);

  const emergencyVisits = useMemo(
    () => (providerScopedVisits || []).filter(
      (v) => v.visit_type === 'EMERGENCY' && (v.status === 'QUEUE' || v.status === 'IN_PROGRESS')
    ),
    [providerScopedVisits]
  );

  // Reset dismissed when new emergencies arrive
  useEffect(() => {
    if (emergencyVisits.length > 0) setEmergencyDismissed(false);
  }, [emergencyVisits.length]);

  // ── Workflow action logging ─────────────────────────
  const { logAction: logWorkflow, lastActions: workflowLastActions } = useWorkflowStore();

  // ── Actions via PUT /api/organisation/visits/:id ─────

  // Req 6 — "Refund applicable" label. Driven by the SAME data the refund modal
  // uses, so the two can never disagree: a visit is flagged iff it has a real
  // collected payment that is refund_eligible (backend refund_policy) AND still
  // has a refundable balance. Receipts are fetched once per visit (cached) for
  // the visits on screen. We deliberately do NOT pre-filter — a cheap filter
  // can't see refundable PROCEDURES on a non-cancelled visit whose sittings
  // aren't loaded, which would make the label disagree with the modal. A visit
  // that collected nothing returns no refundable rows, so it simply never flags.
  const [refundFlags, setRefundFlags] = useState({});   // visit_id -> bool
  const refundCheckedRef = useRef(new Set());           // visit_ids fetched (in-flight guard)
  // bk-017: ONE batch eligibility call for the whole visible page (not per row).
  // STABLE effect dep = a primitive key of the visible visit_ids, so a re-render
  // with the same set never re-fires; the checked-Set guard dedups any partial
  // overlap. A page > the server cap chunks into bounded pages of BATCH_MAX_ITEMS.
  const visibleVisitIdsKey = useMemo(
    () => (filteredVisits || []).map((v) => v?.visit_id).filter(Boolean).join(','),
    [filteredVisits],
  );
  const visitPatientMap = useMemo(() => {
    const m = new Map();
    (filteredVisits || []).forEach((v) => { if (v?.visit_id) m.set(v.visit_id, v.patient_id); });
    return m;
  }, [filteredVisits]);
  useEffect(() => {
    const ids = visibleVisitIdsKey ? visibleVisitIdsKey.split(',') : [];
    if (!ids.length) return;
    const visits = ids.map((id) => ({ visit_id: id, patient_id: visitPatientMap.get(id) }));
    fetchRefundFlags(visits, { checkedSet: refundCheckedRef.current }).then((flags) => {
      if (Object.keys(flags).length) setRefundFlags((prev) => ({ ...prev, ...flags }));
    });
  }, [visibleVisitIdsKey, visitPatientMap]);

  const isRefundApplicable = useCallback((visit) => !!refundFlags[visit?.visit_id], [refundFlags]);

  const handleStart = async (visitId) => {
    await updateVisit(visitId, { status: 'IN_PROGRESS' });
    logWorkflow(visitId, WORKFLOW_ACTIONS.MARK_IN_PROGRESS);
  };

  // Req 6 — completion goes through the shared guard, so an open-procedures
  // confirmation prompts here AND on the doctor's queue screen identically.
  const { requestComplete, completionPromptNode } = useVisitCompletionGuard({
    updateVisit,
    viewer: { memberId, role: userRole },
    onCompleted: (vid) => {
      try { localStorage.removeItem(`rx_draft_${vid}`); } catch { /* */ }
      logWorkflow(vid, WORKFLOW_ACTIONS.MARK_COMPLETED);
    },
  });
  const handleComplete = useCallback(
    (visitId) => requestComplete((visits || []).find((v) => v.visit_id === visitId) || { visit_id: visitId }),
    [requestComplete, visits],
  );
  // Phase 3.1 — Send Visit dialog state
  const [sendVisitDialog, setSendVisitDialog] = useState(null); // visit row
  const handleSendVisitSubmitted = useCallback(() => {
    setSendVisitDialog(null);
    // Visit row mutated server-side (doctor_member_id flip on bypass /
    // overlay set on park). Re-load the list so the row falls out of
    // this user's queue immediately.
    try { loadVisits(); } catch { /* noop */ }
  }, [loadVisits]);
  // Widget Library MVP — Cancel + Follow-Up moved to widgets. The
  // legacy local-state dialogs (followUpDialog + cancelConfirm) and
  // their confirm handlers (confirmFollowUp + confirmCancelVisit) were
  // removed in PR 5; updateVisit / logWorkflow are now called inside
  // FollowUpWidget / VisitCancelWidget which open via sdk.openWidget.
  const handleFollowUp = (visitId) => {
    const visit = (visits || []).find((v) => v.visit_id === visitId);
    const name = visit
      ? (visit.patient_name || `${visit.first_name || ''} ${visit.last_name || ''}`.trim() || 'this patient')
      : 'this patient';
    sdk.openWidget('followUp', {
      visitId,
      prefill: { name },
    }, { source: 'summary' });
  };
  const handleCancel = (visitId) => {
    const visit = (visits || []).find((v) => v.visit_id === visitId);
    const name = visit
      ? (visit.patient_name || `${visit.first_name || ''} ${visit.last_name || ''}`.trim() || 'this patient')
      : 'this patient';
    sdk.openWidget('visitCancel', {
      visitId,
      prefill: { name, userRole },
    }, { source: 'summary' });
  };

  const card = {
    borderRadius: '1rem',
    // 'clip' (not 'hidden') — keeps the rounded-corner clipping WITHOUT
    // creating a scroll container, which is what let the sticky filter bar
    // below actually stick to the viewport instead of this (non-scrolling) card.
    overflow: 'clip',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)',
  };

  const filterInputStyle = {
    padding: '0.375rem 0.625rem',
    borderRadius: '0.5rem',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
    color: isDark ? '#f5f5f7' : '#111827',
    fontSize: '0.75rem',
    outline: 'none',
  };

  // ── Auto-scroll to top when emergency popup appears ──
  useEffect(() => {
    if (emergencyVisits.length > 0 && !emergencyDismissed) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [emergencyVisits.length, emergencyDismissed]);

  // ── Pinned search row (bk-1034 / bk-1038) ──────────────────────────────
  // `position: sticky` cannot engage here: `#root` carries `overflow-x:
  // hidden` (src/index.css), which per the CSS overflow spec forces the
  // computed `overflow-y` to `auto` too — making #root a scroll container
  // and breaking `position: sticky` for every descendant app-wide (see
  // bk-1038; a global fix was considered and declined by the owner — this
  // page gets a LOCAL substitute only, `#root` is untouched).
  //
  // Substitute: the classic sentinel + IntersectionObserver "sticky
  // polyfill" technique. A zero-height sentinel sits right where the row
  // naturally starts; once it scrolls above the viewport's own top edge, the
  // row switches to `position: fixed` at `top: 0`, and a placeholder holds
  // its layout height so the list doesn't jump.
  //
  // Pins at `top: 0`, NOT below `.admin-header` — that header is ALSO broken
  // by bk-1038 (its own `position: sticky` doesn't engage either), so it
  // scrolls away with everything else; offsetting below a header that isn't
  // actually there left the row floating with the previous card showing in
  // the gap above it. If bk-1038 ever makes the header genuinely sticky
  // again, this row's offset must come back.
  const cardRef = useRef(null);
  const rowRef = useRef(null);
  const sentinelRef = useRef(null);
  const [cardRect, setCardRect] = useState({ left: 0, width: 0 });
  const [rowHeight, setRowHeight] = useState(0);
  const [searchRowPinned, setSearchRowPinned] = useState(false);

  // The card's CONTENT-BOX rect — `getBoundingClientRect()` returns the
  // BORDER box, but the static (unpinned) row sits inside the card's content
  // box, flush against its 1px border. Using the border box for the pinned
  // row's `left`/`width` painted it over that 1px border on both sides;
  // `left + clientLeft` skips the border, `clientWidth` is the content(+
  // padding) box width, so pinned and static line up to the pixel.
  const measureCardRect = useCallback(() => {
    if (!cardRef.current) return;
    const el = cardRef.current;
    const r = el.getBoundingClientRect();
    const left = r.left + el.clientLeft;
    const width = el.clientWidth;
    setCardRect((prev) => (prev.left === left && prev.width === width ? prev : { left, width }));
  }, []);

  // Re-measured on resize (rAF-throttled) + the row's own height (for the
  // placeholder).
  useEffect(() => {
    const measure = () => {
      measureCardRect();
      if (rowRef.current) setRowHeight(Math.round(rowRef.current.getBoundingClientRect().height));
    };
    measure();

    let raf = null;
    const onResize = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => { raf = null; measure(); });
    };
    window.addEventListener('resize', onResize, { passive: true });

    return () => {
      window.removeEventListener('resize', onResize);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [measureCardRect]);

  // Pin/unpin. Re-measures the card rect at the MOMENT of pinning — a page
  // scrollbar appearing/disappearing (e.g. the list narrowing while typing)
  // shifts the card without firing a window `resize`, so a resize-only
  // measurement can go stale until the next pin/unpin cycle. Guarded — no
  // IntersectionObserver (jsdom) means the row simply never pins; it stays
  // in flow, no crash.
  useEffect(() => {
    if (!sentinelRef.current || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const pinning = !entry.isIntersecting;
        if (pinning) measureCardRect();
        setSearchRowPinned(pinning);
      },
      { rootMargin: '0px', threshold: 0 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [measureCardRect]);

  return (
    <>
      <style>{`@keyframes mvp-spin { to { transform: rotate(360deg); } }`}</style>
      {/* ── Emergency Alert Popup (rendered outside card to avoid overflow:hidden clipping) ── */}
      {emergencyVisits.length > 0 && !emergencyDismissed && (
        <EmergencyPopup
          emergencyVisits={emergencyVisits}
          isDark={isDark}
          onDismiss={() => setEmergencyDismissed(true)}
          onStart={handleStart}
          loading={loading.update}
        />
      )}

    <div ref={cardRef} style={card}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', padding: '0.875rem 1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.125rem' }}>📋</span>
            <h2 style={{ margin: 0, color: '#fff', fontWeight: 600, fontSize: '1rem' }}>{isScoped ? `${scopeLabel} Visits` : 'Manage Visits'}</h2>
            <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '0.6875rem', padding: '0.125rem 0.5rem', borderRadius: '9999px' }}>
              {filteredVisits.length}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={loadVisits}
              disabled={loading.visits}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              ↻ Refresh
            </button>
            {onBack && (
              <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '0.875rem' }}>✕</button>
            )}
          </div>
        </div>
      </div>
      {/* S13 — names the walled receptionist's scope (visit wall). */}
      <ScopeCaption scoped={deskWalled} className="px-5 pt-2" />

      {/* ── Provider-type filter (primary row) — All / OPD / Nurse / Lab.
            Hidden for scoped (doctor) users, who only ever see their own OPD. */}
      {!isScoped && (
        <div style={{
          padding: '0.625rem 1.25rem 0',
          display: 'flex', gap: '0.375rem', flexWrap: 'wrap', alignItems: 'center',
        }}>
          {PROVIDER_FILTERS.map((pf) => {
            const active = filters.provider === pf.key;
            return (
              <button
                key={pf.key || 'ALL'}
                onClick={() => setFilters((f) => ({ ...f, provider: pf.key, assignee_id: '', type: '', settlement: '' }))}
                style={{
                  padding: '0.3125rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: active ? 700 : 500,
                  border: active
                    ? `1.5px solid ${isDark ? '#3b82f6' : '#2563eb'}`
                    : `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                  background: active
                    ? isDark ? 'rgba(59,130,246,0.18)' : 'rgba(37,99,235,0.1)'
                    : isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
                  color: active ? '#3b82f6' : isDark ? '#e5e7eb' : '#374151',
                  cursor: 'pointer',
                  transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                }}
              >
                {pf.icon ? `${pf.icon} ` : ''}{pf.label}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Pinned patient-search row (bk-1034/bk-1038) — ONLY the search box
          stays pinned while a long queue scrolls; the rest of the filter bar
          below scrolls away as before (owner ruling 2026-09-06). Pins at
          `top: 0` — NOT below `.admin-header`, which is itself broken by
          bk-1038 and scrolls away (see the effect above). Solid background
          in both themes so scrolled-under rows don't bleed through. The ONE
          VisitSearchInput — sizing passed via its existing `style` prop, no
          second implementation, no new props. ── */}
      <div ref={sentinelRef} data-testid="visit-search-sentinel" style={{ height: 0 }} aria-hidden="true" />
      {searchRowPinned && (
        <div data-testid="visit-search-row-placeholder" style={{ height: rowHeight }} aria-hidden="true" />
      )}
      <div
        ref={rowRef}
        data-testid="visit-search-row"
        style={{
          padding: '0.5rem 1.25rem',
          display: 'flex',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          background: isDark ? '#1e293b' : '#ffffff',
          zIndex: 20,
          ...(searchRowPinned
            ? { position: 'fixed', top: 0, left: cardRect.left, width: cardRect.width }
            : { position: 'static' }),
        }}
      >
        {/* D44/S19 — patient search, in its own pinned row (bk-1034/bk-1038). */}
        <VisitSearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          isDark={isDark}
          style={{ flex: '1 1 22rem', maxWidth: '28rem' }}
        />
      </div>

      {/* ── Filter Bar (status sub-filter + assignee + date) ── NOT sticky
          (owner ruling 2026-09-06, AC2) — only the search row above pins. ── */}
      <div
        data-testid="visit-filter-bar"
        style={{
          padding: '0.75rem 1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          flexWrap: 'wrap',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
        }}
      >
        {/* Status chips (sub-filter within the selected provider) */}
        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
          {statusFilters.map((sf) => {
            const active = filters.status === sf.value;
            return (
              <button
                key={sf.value}
                onClick={() => setFilters((f) => ({ ...f, status: sf.value }))}
                style={{
                  padding: '0.25rem 0.625rem',
                  borderRadius: '9999px',
                  fontSize: '0.6875rem',
                  fontWeight: active ? 600 : 400,
                  border: active
                    ? `1.5px solid ${isDark ? '#3b82f6' : '#2563eb'}`
                    : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  background: active
                    ? isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)'
                    : 'transparent',
                  color: active
                    ? '#3b82f6'
                    : isDark ? '#9ca3af' : '#6b7280',
                  cursor: 'pointer',
                  transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                }}
              >
                {sf.label}
              </button>
            );
          })}
        </div>

        {/* Department filter (S1) — reuses the A2 board DeptFilter control.
            Rendered ONLY when the org has ≥1 active department (S1.3); hidden
            for scoped DOCTOR alongside the provider/assignee rows. Switching
            it never fires a network request — visitMatchesDeptFilter runs
            client-side inside providerScopedVisits. */}
        {!isScoped && deptFilterOptions.length > 0 && (
          <DeptFilter
            options={deptFilterOptions}
            value={filters.department_id}
            onChange={handleDeptFilterChange}
          />
        )}

        {/* Assignee filter — provider-aware roster (doctors / nurses / lab techs,
            or a combined list on "All"). Hidden for scoped users. */}
        {!isScoped && (
          <select
            value={filters.assignee_id}
            onChange={(e) => setFilters((f) => ({ ...f, assignee_id: e.target.value }))}
            style={filterInputStyle}
          >
            <option value="">{ASSIGNEE_ALL_LABEL[filters.provider] || 'All Staff'}</option>
            {assigneeOptions.map((m) => (
              <option key={memberIdOf(m)} value={memberIdOf(m)}>{memberName(m)}</option>
            ))}
          </select>
        )}
        {!isScoped && assignableDoctorsError && (
          <span data-testid="assignee-filter-error" style={{ fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>{DOCTORS_LOAD_ERROR_COPY}</span>
        )}

        {/* S8 (EP-4 AC5) — Lab Visit tab Type (IPD/OPD/All) + Settlement
            (Settled/Unsettled/All) lens. Server-side filters (loadVisits
            re-fetches on change, unlike provider/assignee/dept above) — see
            showLabFilters for the scoped-lab-tech carve-out. */}
        {showLabFilters && (
          <select
            data-testid="lab-type-filter"
            aria-label="Lab entry type filter"
            value={filters.type}
            onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
            style={filterInputStyle}
          >
            <option value="">All (IPD + OPD)</option>
            <option value="IPD">IPD</option>
            <option value="OPD">OPD</option>
          </select>
        )}
        {showLabFilters && (
          <select
            data-testid="lab-settlement-filter"
            aria-label="Lab settlement filter"
            value={filters.settlement}
            onChange={(e) => setFilters((f) => ({ ...f, settlement: e.target.value }))}
            style={filterInputStyle}
          >
            <option value="">All settlement</option>
            <option value="SETTLED">Settled</option>
            <option value="UNSETTLED">Unsettled</option>
          </select>
        )}

        {/* Date filter — ADMIN & DOCTOR unrestricted; RECEPTIONIST limited to last 7 days */}
        <input
          type="date"
          value={filters.date}
          onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))}
          disabled={!canPickDate}
          min={dateMin}
          max={dateMax}
          style={{
            ...filterInputStyle,
            ...(!canPickDate ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
          }}
        />

        {/* Export buttons — ADMIN only. Legacy Excel/Busy export is gated on
            `isAdmin`, NOT `canExport` (EXPORT_VISIT) — T2 grants EXPORT_VISIT
            to RECEPTIONIST + DOCTOR for the NEW Print button below, and this
            re-gate keeps the legacy buttons' audience unchanged (ADMIN-only,
            same as before T2; see t2-build-plan.md §SECURITY INTERACTION). */}
        {isAdmin && (
          <>
            <button
              data-testid="export-standard-btn"
              onClick={() => handleExport('standard')}
              disabled={!!exporting}
              title="Export visits as Excel"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.3125rem 0.625rem', borderRadius: '0.5rem', cursor: exporting ? 'not-allowed' : 'pointer',
                fontSize: '0.6875rem', fontWeight: 500,
                border: `1px solid ${isDark ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.3)'}`,
                background: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)',
                color: isDark ? '#4ade80' : '#16a34a',
                opacity: exporting ? 0.6 : 1,
              }}
            >
              {exporting === 'standard' ? <span style={{ display: 'inline-block', width: '0.7rem', height: '0.7rem', border: '1.5px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'mvp-spin 0.7s linear infinite' }} /> : '⬇'} Export
            </button>
            <button
              data-testid="export-busy-btn"
              onClick={() => handleExport('busy')}
              disabled={!!exporting}
              title="Export for Busy/Tally import"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.3125rem 0.625rem', borderRadius: '0.5rem', cursor: exporting ? 'not-allowed' : 'pointer',
                fontSize: '0.6875rem', fontWeight: 500,
                border: `1px solid ${isDark ? 'rgba(234,179,8,0.25)' : 'rgba(234,179,8,0.3)'}`,
                background: isDark ? 'rgba(234,179,8,0.08)' : 'rgba(234,179,8,0.06)',
                color: isDark ? '#fbbf24' : '#b45309',
                opacity: exporting ? 0.6 : 1,
              }}
            >
              {exporting === 'busy' ? <span style={{ display: 'inline-block', width: '0.7rem', height: '0.7rem', border: '1.5px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'mvp-spin 0.7s linear infinite' }} /> : '📊'} Busy
            </button>
          </>
        )}

        {/* Print Queue (S2/S3/S4, +T4 split-button chooser) — gated
            EXPORT_VISIT (ADMIN + RECEPTIONIST + DOCTOR). Main button keeps
            the default A4 behavior (AC1, unchanged); the caret opens a
            2-item menu offering "Print A4" / "Print Thermal (80mm)" (T4
            gate ruling — split button). Relabels to "Print Lab & Diagnostic Queue" when
            the LAB provider filter is active (main button label only, AC7);
            disabled with a tooltip (both formats) when there's nothing in
            scope to print. Scoped DOCTOR sees this control with no doctor
            picker — the server self-scopes the summary. */}
        {canExport && (
          <div ref={printMenuRef} style={{ position: 'relative', display: 'inline-flex' }}>
            <div style={{ display: 'inline-flex', borderRadius: '0.5rem', overflow: 'hidden' }}>
              <button
                data-testid="print-queue-btn"
                onClick={() => handlePrintQueue('a4')}
                disabled={printing || filteredVisits.length === 0}
                title={filteredVisits.length === 0
                  ? 'No visits in the queue to print for the selected filters.'
                  : 'Print the currently filtered queue'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.3125rem 0.625rem',
                  borderRadius: '0.5rem 0 0 0.5rem',
                  cursor: (printing || filteredVisits.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '0.6875rem', fontWeight: 500,
                  border: `1px solid ${isDark ? 'rgba(59,130,246,0.3)' : '#93c5fd'}`,
                  borderRight: 'none',
                  background: isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff',
                  color: isDark ? '#93c5fd' : '#1d4ed8',
                  opacity: (printing || filteredVisits.length === 0) ? 0.6 : 1,
                }}
              >
                {printing ? <span style={{ display: 'inline-block', width: '0.7rem', height: '0.7rem', border: '1.5px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'mvp-spin 0.7s linear infinite' }} /> : '🖨️'}
                {' '}{filters.provider === 'LAB' ? 'Print Lab & Diagnostic Queue' : 'Print Queue'}
              </button>
              <button
                data-testid="print-queue-menu-toggle"
                onClick={() => setPrintMenuOpen((open) => !open)}
                disabled={printing || filteredVisits.length === 0}
                title="Choose print format"
                aria-label="Choose print format"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0.3125rem 0.4375rem',
                  borderRadius: '0 0.5rem 0.5rem 0',
                  cursor: (printing || filteredVisits.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '0.6875rem', fontWeight: 500,
                  border: `1px solid ${isDark ? 'rgba(59,130,246,0.3)' : '#93c5fd'}`,
                  background: isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff',
                  color: isDark ? '#93c5fd' : '#1d4ed8',
                  opacity: (printing || filteredVisits.length === 0) ? 0.6 : 1,
                }}
              >
                ▾
              </button>
            </div>

            {printMenuOpen && (
              <div
                data-testid="print-queue-menu"
                style={{
                  position: 'absolute', top: '100%', marginTop: '0.375rem', left: 0, zIndex: 30,
                  minWidth: '11rem', borderRadius: '0.5rem', padding: '0.25rem',
                  background: isDark ? '#1e293b' : '#ffffff',
                  border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
                  boxShadow: isDark ? '0 10px 24px rgba(0,0,0,0.5)' : '0 10px 24px rgba(15,23,42,0.18)',
                }}
              >
                <div
                  data-testid="print-format-a4"
                  onClick={() => { setPrintMenuOpen(false); handlePrintQueue('a4'); }}
                  style={{
                    padding: '0.5rem 0.625rem', borderRadius: '0.375rem', cursor: 'pointer',
                    fontSize: '0.75rem', fontWeight: 500,
                    color: isDark ? '#e2e8f0' : '#0f172a',
                  }}
                >
                  🖨️ Print A4
                </div>
                <div
                  data-testid="print-format-thermal"
                  onClick={() => { setPrintMenuOpen(false); handlePrintQueue('thermal'); }}
                  style={{
                    padding: '0.5rem 0.625rem', borderRadius: '0.375rem', cursor: 'pointer',
                    fontSize: '0.75rem', fontWeight: 500,
                    color: isDark ? '#e2e8f0' : '#0f172a',
                  }}
                >
                  🧾 Print Thermal (80mm)
                </div>
              </div>
            )}
          </div>
        )}

        {/* Print error */}
        {printError && (
          <span style={{ fontSize: '0.6875rem', color: isDark ? '#fca5a5' : '#991b1b' }}>
            ⚠️ Print failed: {printError}
          </span>
        )}
      </div>

      {/* Daily collection summary — Phase 12.6. Permission-gated so
          doctors/nurses never see it; financial totals are a front-desk
          / management concern. Follows the page's date + doctor filters
          and auto-refreshes on bundled-payment realtime events. */}
      {canSeeDailySummary && (
        <div style={{ padding: '0 1.25rem' }}>
          <DailyCollectionSummary
            date={filters.date}
            doctorId={paymentsDoctorId}
            provider={filters.provider || null}
            isDark={isDark}
          />
        </div>
      )}

      {/* Export error */}
      {exportError && (
        <div style={{ padding: '0.5rem 1.25rem', background: isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2', borderBottom: `1px solid ${isDark ? 'rgba(239,68,68,0.15)' : '#fecaca'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.8125rem', color: isDark ? '#fca5a5' : '#991b1b' }}>⚠️ Export failed: {exportError}</span>
          <button onClick={() => setExportError(null)} style={{ background: 'none', border: 'none', color: isDark ? '#fca5a5' : '#ef4444', cursor: 'pointer', fontSize: '0.6875rem' }}>✕</button>
        </div>
      )}

      {/* Success */}
      {successMessage && (
        <div style={{ padding: '0.625rem 1.25rem', background: isDark ? 'rgba(34,197,94,0.1)' : '#f0fdf4', borderBottom: `1px solid ${isDark ? 'rgba(34,197,94,0.2)' : '#dcfce7'}`, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>✅</span>
          <span style={{ fontSize: '0.8125rem', color: isDark ? '#4ade80' : '#166534' }}>{successMessage}</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: '0.625rem 1.25rem', background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2', borderBottom: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.8125rem', color: isDark ? '#fca5a5' : '#991b1b' }}>⚠️ {error}</span>
          <button onClick={clearError} style={{ background: 'none', border: 'none', color: isDark ? '#fca5a5' : '#ef4444', cursor: 'pointer', fontSize: '0.6875rem' }}>Dismiss</button>
        </div>
      )}

      {/* Loading */}
      {loading.visits && filteredVisits.length === 0 && (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <MiniSpinner />
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#6b7280' }}>Loading visits...</p>
        </div>
      )}

      {/* D44/S19 — what the search is showing, in the same shape as the
          templates list's count line. Only while the box has text. */}
      {!loading.visits && searchActive && filteredVisits.length > 0 && (
        <div
          data-testid="visit-search-count"
          style={{
            padding: '0.25rem 0.75rem 0', fontSize: '0.75rem',
            color: isDark ? '#94a3b8' : '#64748b', fontFamily: 'system-ui, sans-serif',
          }}
        >
          {VISIT_SEARCH_COPY.count(filteredVisits.length, statusScopedVisits.length)}
        </div>
      )}

      {/* Empty State */}
      {!loading.visits && filteredVisits.length === 0 && (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <span style={{ fontSize: '2.5rem' }}>🏥</span>
          {searchActive ? (
            // …the SEARCH found nothing. Naming the other filters is what keeps
            // the doctor from thinking the visit is gone when a date or status
            // filter is what hid it.
            <p
              data-testid="visit-search-empty"
              style={{ margin: '0.75rem 0 0', fontSize: '0.875rem', fontWeight: 500, color: isDark ? '#e8e9ed' : '#374151' }}
            >
              {VISIT_SEARCH_COPY.empty(searchTerm.trim(), otherFiltersActive)}
            </p>
          ) : (
            <>
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.875rem', fontWeight: 500, color: isDark ? '#e8e9ed' : '#374151' }}>No visits found</p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: isDark ? '#6b7280' : '#9ca3af' }}>Try adjusting your filters or register a new visit</p>
            </>
          )}
        </div>
      )}

      {/* Smart Suggestion Bar — one per doctor/user, not per visit */}
      {activeVisit && (
        <SmartSuggestionBar
          visit={activeVisit}
          userRole={userRole}
          lastCompletedAction={workflowLastActions[activeVisit.visit_id]}
          onStartVisit={(id) => handleStart(id)}
          onCompleteVisit={(id) => handleComplete(id)}
          onCancelVisit={(id) => handleCancel(id)}
          onOpenDetails={(id) => handleToggleDetails(id)}
          onOpenHistory={() => setHistoryPatient({
            patient_id: activeVisit.patient_id,
            patient_name: activeVisit.patient_name || `${activeVisit.first_name || ''} ${activeVisit.last_name || ''}`.trim(),
            mobile: activeVisit.patient_mobile,
          })}
          onOpenVitals={() => {
            setExpandedVisitId(activeVisit.visit_id);
          }}
          onOpenNotes={() => {
            setExpandedVisitId(activeVisit.visit_id);
          }}
          onOpenSymptoms={() => {
            setExpandedVisitId(activeVisit.visit_id);
          }}
          onOpenPrescription={() => {
            setUploadPrescriptionVisitId(activeVisit.visit_id);
          }}
          onOpenReport={() => {
            setExpandedVisitId(activeVisit.visit_id);
          }}
          onGeneratePrescription={(id) => handleGeneratePdf(id)}
        />
      )}

      {/* Visit Cards */}
      <div ref={visitListRef}>
        {visitRows.map((item) => {
          if (item.type === 'header') {
            return (
              <div
                key={item.key}
                data-testid={item.testId}
                style={{
                  padding: '0.5rem 1.25rem',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  background: isDark ? '#1e293b' : '#f8fafc',
                  color: isDark ? '#94a3b8' : '#475569',
                  borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                  borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                }}
              >
                {item.title}
              </div>
            );
          }
          const { visit, idx } = item;
          return (
          <div
            key={visit.visit_id}
            data-visit-id={visit.visit_id}
            style={{
              padding: '1rem 1.25rem',
              borderTop: idx > 0 ? `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` : 'none',
              transition: 'background 0.15s',
              ...(visit.visit_id === initialFocusVisitId ? {
                background: isDark ? 'rgba(13, 148, 136, 0.06)' : 'rgba(13, 148, 136, 0.04)',
              } : {}),
            }}
          >
            {/* Phase 10b — caller's POV badge on forwarded rows. The card
                body below still shows the CURRENT primary's queue/name
                (universal facts), so this badge surfaces the original
                consultation context so the doctor doesn't confuse the
                row with a fresh queue entry. */}
            {visit.viewer_relationship === 'forwarded' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                  padding: '0.375rem 0.625rem',
                  marginBottom: '0.625rem',
                  borderRadius: '0.375rem',
                  background: isDark ? 'rgba(15,118,110,0.22)' : '#ccfbf1',
                  border: `1px solid ${isDark ? 'rgba(45,212,191,0.35)' : '#5eead4'}`,
                  fontSize: '0.6875rem',
                  color: isDark ? '#99f6e4' : '#115e59',
                  fontWeight: 600,
                }}
              >
                <span style={{ fontSize: '0.75rem' }}>→</span>
                <span>
                  Forwarded to Dr. {visit.forwarded_to_doctor_name || visit.doctor_name || 'another doctor'}
                  {visit.forwarded_at ? ` at ${formatVisitTime(visit.forwarded_at)}` : ''}
                </span>
                {(visit.viewer_original_queue_number !== null
                  && visit.viewer_original_queue_number !== undefined) && (
                  <>
                    <span style={{ opacity: 0.6 }}>·</span>
                    <span>
                      Your queue was #{Math.trunc(Number(visit.viewer_original_queue_number))}
                    </span>
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
              {/* Left: Patient Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <h3 data-testid="visit-row-name" style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>
                    {visit.patient_name || `${visit.first_name || ''} ${visit.last_name || ''}`.trim()}
                  </h3>
                  {isRefundApplicable(visit) && (
                    <span
                      title="A paid item on this visit is cancelled or not yet started — a refund may be applicable."
                      style={{
                        fontSize: '0.625rem', fontWeight: 700, padding: '0.0625rem 0.375rem',
                        borderRadius: '9999px',
                        background: isDark ? 'rgba(245,158,11,0.25)' : '#fef3c7',
                        color: isDark ? '#fcd34d' : '#92400e',
                        border: `1px solid ${isDark ? 'rgba(245,158,11,0.4)' : '#fde68a'}`,
                      }}
                    >
                      Refund applicable
                    </span>
                  )}
                  {isFollowUpSet(visit) && (
                    <span
                      title="A follow-up is suggested for this patient."
                      style={{
                        fontSize: '0.625rem', fontWeight: 700, padding: '0.0625rem 0.375rem',
                        borderRadius: '9999px',
                        background: isDark ? 'rgba(20,184,166,0.25)' : '#ccfbf1',
                        color: isDark ? '#5eead4' : '#0f766e',
                        border: `1px solid ${isDark ? 'rgba(20,184,166,0.4)' : '#99f6e4'}`,
                      }}
                    >
                      Follow-up{visit.follow_up_date ? ` · ${visit.follow_up_date}` : ''}
                    </span>
                  )}
                  {/* Age / Gender chips — read-only display */}
                  {(visit.patient_age || visit.patient_gender) && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.1875rem',
                      padding: '0.0625rem 0.3125rem', borderRadius: '9999px',
                      background: isDark ? 'rgba(139,92,246,0.12)' : 'rgba(139,92,246,0.08)',
                      color: isDark ? '#c4b5fd' : '#7c3aed',
                      fontSize: '0.5625rem', fontWeight: 600,
                    }}>
                      {visit.patient_age || ''}{visit.patient_age && visit.patient_gender ? '/' : ''}{visit.patient_gender === 'MALE' ? 'M' : visit.patient_gender === 'FEMALE' ? 'F' : visit.patient_gender === 'OTHER' ? 'O' : (visit.patient_gender || '')}
                    </span>
                  )}
                  <PhoneButton phoneNumber={visit.patient_mobile} isDark={isDark} size="sm" />
                  <Badge
                    text={VISIT_TYPE_LABELS[visit.visit_type] || visit.visit_type}
                    colors={VISIT_TYPE_COLORS[visit.visit_type] || VISIT_TYPE_COLORS.REGULAR}
                  />
                  {/* S8 — IPD lab entry tag (order-sourced from an admission's
                      lab orders; distinct from a regular OPD lab visit). */}
                  {visit.is_ipd_lab && (
                    <span
                      title="Billed via the admission's invoice — settled through IPD credit, not this visit."
                      style={{
                        fontSize: '0.625rem', fontWeight: 700, padding: '0.0625rem 0.375rem',
                        borderRadius: '9999px', textTransform: 'uppercase', letterSpacing: '0.04em',
                        background: isDark ? 'rgba(99,102,241,0.28)' : '#e0e7ff',
                        color: isDark ? '#c7d2fe' : '#4338ca',
                        border: `1px solid ${isDark ? 'rgba(129,140,248,0.45)' : '#a5b4fc'}`,
                      }}
                    >
                      IPD
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.375rem', flexWrap: 'wrap' }}>
                  <ProviderTag visit={visit} isDark={isDark} />
                  {visit.queue_number && (
                    <span style={{
                      fontFamily: 'monospace',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      padding: '0.125rem 0.5rem',
                      borderRadius: '0.375rem',
                      background: isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6',
                      color: isDark ? '#e8e9ed' : '#374151',
                    }}>
                      #{visit.queue_number}
                    </span>
                  )}
                  {visit.visit_time && (
                    <span style={{ fontSize: '0.6875rem', color: isDark ? '#6b7280' : '#9ca3af', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      🕐 {formatVisitTime(visit.visit_time)}
                    </span>
                  )}
                  {/* Receipt number */}
                  {visit.receipt_number && (
                    <span style={{
                      fontFamily: 'monospace', fontSize: '0.6875rem', fontWeight: 700,
                      padding: '0.125rem 0.5rem', borderRadius: '0.375rem',
                      background: isDark ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.06)',
                      color: isDark ? '#c4b5fd' : '#7c3aed',
                    }}>
                      {visit.receipt_number}
                    </span>
                  )}
                  {/* S8 — IPD lab settlement status (SEAM-B, shares the
                      invoice's stored-payment_id classifier — S8 AC7).
                      ADMISSION-ACCESS: null = the stay hides its money from
                      this viewer → no chip (never a fake UNSETTLED). */}
                  {visit.is_ipd_lab && visit.settlement_status != null && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
                      fontSize: '0.6875rem', fontWeight: 700,
                      padding: '0.125rem 0.5rem', borderRadius: '0.375rem',
                      background: visit.settlement_status === 'SETTLED'
                        ? (isDark ? 'rgba(34,197,94,0.18)' : '#dcfce7')
                        : (isDark ? 'rgba(245,158,11,0.18)' : '#fef3c7'),
                      color: visit.settlement_status === 'SETTLED'
                        ? (isDark ? '#4ade80' : '#166534')
                        : (isDark ? '#fbbf24' : '#92400e'),
                    }}>
                      {visit.settlement_status === 'SETTLED'
                        ? `SETTLED — ${formatSettlementDate(visit.settlement_date)}`
                        : 'UNSETTLED'}
                    </span>
                  )}
                  {/* C1 — IPD-lab row amount. Reads the server `net_rupees`
                      verbatim (single source, never re-derived client-side —
                      same field FE-1's print sheet binds to). Was previously
                      a bare "0"/absent for every synthesized IPD-lab row. */}
                  {visit.is_ipd_lab && visit.settlement_status === 'SETTLED' && visit.net_rupees != null && (
                    <span style={{
                      fontFamily: 'monospace', fontSize: '0.6875rem', fontWeight: 700,
                      padding: '0.125rem 0.5rem', borderRadius: '0.375rem',
                      background: isDark ? 'rgba(34,197,94,0.14)' : '#dcfce7',
                      color: isDark ? '#4ade80' : '#166534',
                    }}>
                      {formatRupees(visit.net_rupees)}
                    </span>
                  )}
                  {/* C1 — unsettled receivable, muted (memo-only — never
                      folded into a collected total). // COPY: Business-ratified
                      ("receivable ₹X" wording). */}
                  {visit.is_ipd_lab && visit.settlement_status === 'UNSETTLED' && visit.unbilled_amount_rupees != null && (
                    <span
                      title="Receivable — not yet collected; excluded from today's collected total."
                      style={{
                        fontFamily: 'monospace', fontSize: '0.6875rem', fontWeight: 600,
                        padding: '0.125rem 0.5rem', borderRadius: '0.375rem',
                        background: isDark ? 'rgba(148,163,184,0.16)' : '#f1f5f9',
                        color: isDark ? '#cbd5e1' : '#475569',
                      }}
                    >
                      receivable {formatRupees(visit.unbilled_amount_rupees)}
                    </span>
                  )}
                  {/* Payment mode */}
                  {visit.payment_mode && (() => {
                    const pm = PAYMENT_MODES.find((p) => p.id === visit.payment_mode);
                    if (!pm) return null;
                    return (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
                        fontSize: '0.6875rem', fontWeight: 600,
                        padding: '0.125rem 0.4rem', borderRadius: '0.375rem',
                        background: isDark ? 'rgba(20,184,166,0.1)' : 'rgba(20,184,166,0.07)',
                        color: isDark ? '#2dd4bf' : '#0d9488',
                      }}>
                        {pm.icon} {pm.label}
                      </span>
                    );
                  })()}
                </div>
              </div>

              {/* Right: Status */}
              <Badge
                text={visit.status}
                colors={STATUS_COLORS[visit.status] || STATUS_COLORS.QUEUE}
                bold
              />
            </div>

            {/* Cancellation info for cancelled visits */}
            {visit.status === 'CANCELLED' && (visit.cancellation_reason || visit.cancelled_by_role) && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.5rem 0.625rem',
                borderRadius: '0.5rem',
                background: isDark ? 'rgba(239,68,68,0.06)' : 'rgba(239,68,68,0.04)',
                border: `1px solid ${isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)'}`,
              }}>
                {visit.cancelled_by_role && (
                  <span style={{
                    fontSize: '0.6875rem', fontWeight: 600,
                    color: '#ef4444',
                    textTransform: 'capitalize',
                  }}>
                    Cancelled by {visit.cancelled_by_role.toLowerCase()}
                  </span>
                )}
                {visit.cancellation_reason && (
                  <p style={{
                    margin: visit.cancelled_by_role ? '0.25rem 0 0' : 0,
                    fontSize: '0.75rem',
                    color: isDark ? '#9ca3af' : '#6b7280',
                    lineHeight: 1.4,
                    fontStyle: 'italic',
                  }}>
                    "{visit.cancellation_reason}"
                  </p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
              {(canManageQueue || canApproveVisit) && visit.status === 'QUEUE' && (
                <ActionButton onClick={() => handleStart(visit.visit_id)} loading={loading.update} color="#0d9488" isDark={isDark}>
                  ▶ Start
                </ActionButton>
              )}
              {canCancelVisit && visit.status === 'QUEUE' && (
                <ActionButton onClick={() => handleCancel(visit.visit_id)} loading={loading.update} color="#6b7280" isDark={isDark}>
                  ✕ Cancel
                </ActionButton>
              )}
              {(canManageQueue || canCompleteVisit) && visit.status === 'IN_PROGRESS' && (
                <ActionButton onClick={() => handleComplete(visit.visit_id)} loading={loading.update} color="#22c55e" isDark={isDark}>
                  ✓ Complete
                </ActionButton>
              )}
              {/* Phase 3.1 — unified Send Visit (refer / send for action).
                  Gate is intentionally broader than just `canReferVisit` so
                  the button still surfaces for doctors whose session was
                  issued before REFER_VISIT was added to their role grants
                  (cached / stale session). The backend re-enforces the
                  actual permission on POST /visits/:id/refer — clicking
                  without authority returns a 403 that the dialog surfaces. */}
              {(canReferVisit || canCompleteVisit || canManageQueue)
                && visit.status === 'IN_PROGRESS' && (
                <ActionButton
                  onClick={() => setSendVisitDialog(visit)}
                  loading={false}
                  color="#d97706"
                  isDark={isDark}
                >
                  ➤ Send Visit
                </ActionButton>
              )}
              {canCancelVisit && visit.status === 'IN_PROGRESS' && (
                <ActionButton onClick={() => handleCancel(visit.visit_id)} loading={loading.update} color="#6b7280" isDark={isDark}>
                  ✕ Cancel
                </ActionButton>
              )}

              {/* Resume Visit — second discovery path for the doctor /
                  nurse who lands on a COMPLETED visit (deep link, patient
                  history, etc.) and finds it has procedures awaiting
                  their action. Same-day only; backend re-enforces both
                  conditions and rejects cross-day with 409. Gated on
                  EXECUTE_SITTING (matches the /resume endpoint's perm)
                  so the button stays hidden for receptionists who don't
                  hold it. */}
              {canExecuteSitting
                && visit.status === 'COMPLETED'
                && visit.visit_date
                && visit.visit_date === new Date().toISOString().slice(0, 10) && (
                <ActionButton
                  onClick={() => {
                    requestResume(visit, {
                      afterResume: () => {
                        // Stay on this page; the WS VISIT_STATUS_CHANGED
                        // refetch will flip the visit's status here too.
                      },
                    }).catch((err) => {
                      const msg =
                        err?.message
                        || err?.detail
                        || 'Could not resume the visit.';
                      // eslint-disable-next-line no-alert
                      window.alert(msg);
                    });
                  }}
                  loading={loading.update}
                  color="#0d9488"
                  isDark={isDark}
                >
                  ↻ Resume Visit
                </ActionButton>
              )}

              {/* Receipts modal trigger — ADMIN + RECEPTIONIST. The
                  Receipts and Refund flows live in the same modal but
                  are reached from separate, side-by-side buttons so
                  refund is a deliberate (not buried) action. */}
              {canPrintToken && (
                <button
                  onClick={() => setReceiptsModalVisit({ visit, mode: 'receipts' })}
                  title="Receipts"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.375rem 0.625rem', borderRadius: '0.5rem',
                    fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer', border: 'none',
                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    color: isDark ? '#9ca3af' : '#6b7280',
                  }}
                >
                  🖨️
                </button>
              )}

              {/* Payments modal trigger — separate from Receipts. Gated
                  by REFUND_PAYMENT so doctors/nurses don't see it. The
                  modal opens in 'refund' mode but the body is now a
                  full payment-management view (Collected / Refunded /
                  Net tiles + per-receipt status + inline refund forms). */}
              {/* Owner ruling — "for IPD, let's disable refund button for
                  labs." Server refuses the write regardless (payment_service
                  .refund reusing lab_settlement.is_ipd_lab); disabled here so
                  staff never open the modal just to discover that. Reuses
                  the SAME `is_ipd_lab` discriminator already stamped on the
                  row (S8) — never re-derived. COPY: PROVISIONAL, NEEDS-PO. */}
              {canRefundPayment && (
                <button
                  onClick={() => {
                    if (visit.is_ipd_lab) return;
                    setReceiptsModalVisit({ visit, mode: 'refund' });
                  }}
                  disabled={visit.is_ipd_lab}
                  title={visit.is_ipd_lab
                    ? "Refunds aren't available for IPD lab charges — billed through the admission's invoice."
                    : 'Payments / Refund'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.375rem 0.625rem', borderRadius: '0.5rem',
                    fontSize: '0.75rem', fontWeight: 500,
                    cursor: visit.is_ipd_lab ? 'not-allowed' : 'pointer', border: 'none',
                    background: visit.is_ipd_lab
                      ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)')
                      : (isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)'),
                    color: visit.is_ipd_lab
                      ? (isDark ? '#6b7280' : '#9ca3af')
                      : (isDark ? '#fca5a5' : '#b91c1c'),
                    opacity: visit.is_ipd_lab ? 0.7 : 1,
                  }}
                >
                  ↩️
                </button>
              )}

              {/* Visit Metadata Details toggle — shown for active visits, hidden for Admin */}
              {userRole !== 'ADMIN' && (visit.status === 'QUEUE' || visit.status === 'IN_PROGRESS' || visit.status === 'COMPLETED') && (() => {
                const isExpanded = expandedVisitId === visit.visit_id;
                const unseenCount = getUnseenCount(visit.visit_id);
                const visitHasDetails = hasDetails(visit.visit_id);
                // Determine button color: unseen = blue pulse, has details = subtle blue, default = gray
                const detailColor = unseenCount > 0
                  ? '#3b82f6'
                  : isExpanded
                  ? '#3b82f6'
                  : visitHasDetails
                  ? '#0d9488'
                  : '#6b7280';

                return (
                  <>
                  <button
                    onClick={() => handleToggleDetails(visit.visit_id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.375rem 0.75rem',
                      borderRadius: '0.5rem',
                      fontSize: '0.75rem',
                      fontWeight: (unseenCount > 0 || visitHasDetails) ? 600 : 500,
                      cursor: 'pointer',
                      border: 'none',
                      background: unseenCount > 0
                        ? `${detailColor}22`
                        : visitHasDetails
                        ? isDark ? 'rgba(13, 148, 136,0.1)' : 'rgba(13, 148, 136,0.06)'
                        : `${detailColor}11`,
                      color: detailColor,
                      transition: 'background 0.15s, color 0.15s',
                      position: 'relative',
                    }}
                  >
                    {isExpanded ? '▲ Close' : '▼ Details'}
                    {/* Unseen counter badge */}
                    {unseenCount > 0 && !isExpanded && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '1rem',
                        height: '1rem',
                        padding: '0 0.25rem',
                        borderRadius: '9999px',
                        background: '#3b82f6',
                        color: '#fff',
                        fontSize: '0.5625rem',
                        fontWeight: 700,
                        marginLeft: '0.25rem',
                        animation: 'pulse 2s ease-in-out 3',
                      }}>
                        {unseenCount}
                      </span>
                    )}
                    {/* Small dot indicator — has details but all seen */}
                    {unseenCount === 0 && visitHasDetails && !isExpanded && (
                      <span style={{
                        width: '0.375rem',
                        height: '0.375rem',
                        borderRadius: '50%',
                        background: '#0d9488',
                        marginLeft: '0.25rem',
                        flexShrink: 0,
                      }} />
                    )}
                  </button>

                  {/* Upload Prescription Button */}
                  {canUploadPrescription && (
                    <button
                      onClick={() => setUploadPrescriptionVisitId(
                        uploadPrescriptionVisitId === visit.visit_id ? null : visit.visit_id
                      )}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.375rem 0.75rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.75rem',
                        fontWeight: uploadPrescriptionVisitId === visit.visit_id ? 600 : 500,
                        cursor: 'pointer',
                        border: uploadPrescriptionVisitId === visit.visit_id
                          ? `1.5px solid ${isDark ? '#0d9488' : '#0f766e'}`
                          : 'none',
                        background: uploadPrescriptionVisitId === visit.visit_id
                          ? isDark ? 'rgba(13, 148, 136,0.15)' : 'rgba(15, 118, 110,0.08)'
                          : isDark ? 'rgba(13, 148, 136,0.1)' : 'rgba(13, 148, 136,0.06)',
                        color: isDark ? '#2dd4bf' : '#0f766e',
                        transition: 'background 0.15s, border-color 0.15s',
                      }}
                    >
                      {uploadPrescriptionVisitId === visit.visit_id ? '▲ Close' : ((userRole || '').toUpperCase() === STAFF_ROLES.DOCTOR ? '💊 Prescription' : '📤 Upload Prescription')}
                    </button>
                  )}

                  {/* View prescription — the visit row's own copy of the
                      read action (the fourth surface; not in the task's
                      inventory, found by grep). It prints an ALREADY-SAVED
                      prescription, so it takes the same verb as the card and
                      the visit panel (owner, this task). */}
                  {canGeneratePdf && (
                    <button
                      onClick={() => handleGeneratePdf(visit.visit_id)}
                      disabled={pdfGenerating === visit.visit_id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.375rem 0.75rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: pdfGenerating === visit.visit_id ? 'wait' : 'pointer',
                        border: `1px solid ${isDark ? 'rgba(220,38,38,0.25)' : 'rgba(220,38,38,0.2)'}`,
                        background: isDark ? 'rgba(220,38,38,0.08)' : 'rgba(220,38,38,0.05)',
                        color: isDark ? '#fca5a5' : '#dc2626',
                        transition: 'background 0.15s, opacity 0.15s',
                        opacity: pdfGenerating === visit.visit_id ? 0.6 : 1,
                      }}
                    >
                      {pdfGenerating === visit.visit_id ? '⏳ Preparing…' : '📄 View prescription'}
                    </button>
                  )}
                  {/* PDF error message */}
                  {pdfError && pdfError.visitId === visit.visit_id && (
                    <span style={{
                      fontSize: '0.625rem',
                      color: isDark ? '#fca5a5' : '#dc2626',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.375rem',
                      background: isDark ? 'rgba(220,38,38,0.08)' : 'rgba(220,38,38,0.04)',
                    }}>
                      ⚠️ {pdfError.message}
                    </span>
                  )}

                  {/* Follow Up Button — only show when follow-up NOT already set (e.g. via digital prescription) */}
                  {(canManageQueue || canCompleteVisit) && visit.status === 'IN_PROGRESS' && !isFollowUpSet(visit) && (
                    <button
                      onClick={() => handleFollowUp(visit.visit_id)}
                      disabled={loading.update}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.375rem 0.75rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: loading.update ? 'wait' : 'pointer',
                        border: `1px solid ${isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.15)'}`,
                        background: isDark ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.05)',
                        color: isDark ? '#60a5fa' : '#3b82f6',
                        transition: 'background 0.15s, opacity 0.15s',
                      }}
                    >
                      🔄 Follow Up
                    </button>
                  )}
                  {/* Follow-up status now shows as a label beside the patient name
                      (isFollowUpSet) — single indicator per card, no duplicate here. */}

                  {/* History Button */}
                  {canViewHistory && (
                  <button
                    onClick={() => {
                      logWorkflow(visit.visit_id, WORKFLOW_ACTIONS.VIEW_PATIENT_HISTORY);
                      setHistoryPatient({
                        patient_id: visit.patient_id,
                        patient_name: visit.patient_name || `${visit.first_name || ''} ${visit.last_name || ''}`.trim(),
                        mobile: visit.patient_mobile,
                      });
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.375rem 0.75rem',
                      borderRadius: '0.5rem',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      border: 'none',
                      background: isDark ? 'rgba(20,184,166,0.1)' : 'rgba(20,184,166,0.06)',
                      color: '#14b8a6',
                      transition: 'background 0.15s, opacity 0.15s',
                    }}
                  >
                    📋 History
                  </button>
                  )}
                  </>
                );
              })()}
            </div>



            {/* Upload Prescription Panel — expanded inline */}
            {uploadPrescriptionVisitId === visit.visit_id && (
              <UploadPrescriptionPanel
                visit={visit}
                isDark={isDark}
                userRole={userRole}
                onSuccess={() => {
                  // Keep panel open to allow more uploads
                }}
                onClose={() => setUploadPrescriptionVisitId(null)}
              />
            )}

            {/* Visit Actions Panel — expanded inline */}
            {expandedVisitId === visit.visit_id && (
              <>
                {/* Phase 3.2/3.3 — visit journey strip; self-hides for
                    untouched visits. Admin sees the headline + can act
                    when they hold the overlay. */}
                <VisitJourney
                  visitId={visit.visit_id}
                  viewerMemberId={memberId}
                  viewerRole={userRole}
                  compact
                />
                {/* Phase 3.1 — incoming referral context for the
                    receiving doctor (silent for non-referral visits). */}
                <IncomingReferralBanner
                  visit={visit}
                  viewerId={memberId}
                  onJumpToRx={() => {
                    // Best-effort scroll into the panel's Rx area.
                    const el = document.querySelector(
                      `[data-rx-panel="${visit.visit_id}"]`,
                    );
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                />
                {/* F8.5 + F9 — prominent "Open tooth chart" CTA for
                    dentists. Lives directly above the findings summary
                    so it's the obvious first chairside action.
                    Dispatches `arogya:open-tooth-chart` which
                    ToothFindingsSummary listens for. Dentist-only;
                    non-dental staff never see it. */}
                {isDentist && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        try {
                          window.dispatchEvent(new CustomEvent(
                            'arogya:open-tooth-chart',
                            { detail: { visitId: visit?.visit_id } },
                          ));
                        } catch (_e) { /* CustomEvent unsupported */ }
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/40 text-teal-800 dark:text-teal-100 hover:bg-teal-100 dark:hover:bg-teal-900/60"
                      title="Open tooth chart to record diagnosis or plan a procedure"
                    >
                      <span aria-hidden="true">🦷</span>
                      <span>Open tooth chart</span>
                    </button>
                  </div>
                )}
                {/* Phase 3.1.1 — pinned tooth-findings summary (auto-
                    shown for any patient with tooth-tagged data). */}
                <ToothFindingsSummary
                  visit={visit}
                  viewer={{ memberId, role: userRole }}
                  isDark={isDark}
                />
                <VisitActionsPanel
                  visit={visit}
                  userRole={userRole}
                  onClose={() => setExpandedVisitId(null)}
                  onMetadataLoaded={(count) => markHasDetails(visit.visit_id, count > 0)}
                  // R5 (owner) — Manage Visits "▼ Details" is read-only for the
                  // RECEPTIONIST (reuses the single `isReceptionist` derivation
                  // above, single source — not a second role check). Every
                  // other VisitActionsPanel mount (ReceptionistDashboard,
                  // NurseTaskCards, DoctorQueueView, AdminSummary) never passes
                  // this prop and stays byte-identical.
                  readOnly={isReceptionist}
                />
              </>
            )}
          </div>
          );
        })}
      </div>
    </div>

    {/* Patient History Modal */}
    {historyPatient && (
      <PatientHistory
        patientId={historyPatient.patient_id}
        patientName={historyPatient.patient_name}
        phoneNumber={historyPatient.mobile}
        onClose={() => setHistoryPatient(null)}
      />
    )}

    {/* Cancel + Follow-Up dialogs render via the widget overlay in
        App.js. Opened by handleCancel / handleFollowUp through
        sdk.openWidget(...) — no local state needed. */}

    {/* Phase 3.1 — Send Visit (refer / send for action) */}
    {sendVisitDialog && (
      <SendVisitDialog
        visit={sendVisitDialog}
        viewer={{ memberId, role: userRole }}
        onSubmitted={handleSendVisitSubmitted}
        onCancel={() => setSendVisitDialog(null)}
      />
    )}

    {/* Phase 12.5 — Receipts / Refund modal. The `mode` is set by the
        triggering button so the user lands directly on the right view. */}
    {receiptsModalVisit && (
      <VisitReceiptsModal
        visitId={receiptsModalVisit.visit.visit_id}
        visit={receiptsModalVisit.visit}
        mode={receiptsModalVisit.mode}
        isOpen={!!receiptsModalVisit}
        onClose={() => setReceiptsModalVisit(null)}
        isDark={isDark}
        departmentName={deptNameById[receiptsModalVisit.visit.department_id]}
        branding={branding}
      />
    )}

    {/* Resume conflict dialog — only renders when the doctor clicks
        Resume Visit while they already have ANOTHER visit IN_PROGRESS.
        Pure prop-driven; no state change unless the doctor confirms a
        Park or the resume succeeds. */}
    <ResumeConflictDialog {...resumeDialogProps} />

    {/* Req 6 — open-procedures confirmation when completing a visit. */}
    {completionPromptNode}
    </>
  );
}

function Badge({ text, colors, bold }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '0.125rem 0.5rem',
      borderRadius: '9999px',
      fontSize: '0.625rem',
      fontWeight: bold ? 700 : 500,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      background: colors.bg,
      color: colors.text,
    }}>
      {text}
    </span>
  );
}

function ActionButton({ children, onClick, loading: isLoading, color, isDark }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.375rem 0.75rem',
        borderRadius: '0.5rem',
        fontSize: '0.75rem',
        fontWeight: 500,
        cursor: isLoading ? 'not-allowed' : 'pointer',
        opacity: isLoading ? 0.5 : 1,
        border: 'none',
        background: hovered ? `${color}22` : `${color}11`,
        color: color,
        transition: 'background 0.15s, opacity 0.15s',
      }}
    >
      {isLoading ? <MiniSpinner /> : children}
    </button>
  );
}

function MiniSpinner() {
  return (
    <svg className="animate-spin" style={{ width: '0.875rem', height: '0.875rem' }} viewBox="0 0 24 24" fill="none">
      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

/**
 * ── Emergency Alert Popup ────────────────────────────────────────────
 * Full-screen overlay that auto-triggers when emergency visits exist.
 * Shows patient name, doctor, queue# with high-urgency red styling.
 */
function EmergencyPopup({ emergencyVisits, isDark, onDismiss, onStart, loading: isLoading }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)',
        padding: '1rem',
        animation: 'fadeIn 0.25s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '420px',
          borderRadius: '1.25rem',
          overflow: 'hidden',
          background: isDark ? '#1e1e24' : '#fff',
          boxShadow: '0 8px 24px rgba(239,68,68,0.2)',
          border: '2px solid rgba(239,68,68,0.4)',
        }}
      >
        {/* Red Pulsing Header */}
        <div style={{
          background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
          padding: '1.25rem 1.25rem',
          textAlign: 'center',
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.1)',
            animation: 'pulse 2s ease-in-out 5',
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <span style={{ fontSize: '2rem', display: 'block', marginBottom: '0.375rem' }}>🚨</span>
            <h3 style={{ margin: 0, color: '#fff', fontSize: '1.125rem', fontWeight: 700 }}>
              Emergency {emergencyVisits.length === 1 ? 'Visit' : `Visits (${emergencyVisits.length})`}
            </h3>
            <p style={{ margin: '0.25rem 0 0', color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem' }}>
              Requires immediate attention
            </p>
          </div>
        </div>

        {/* Emergency Visit Cards */}
        <div style={{ padding: '0.75rem 1.25rem', maxHeight: '300px', overflowY: 'auto' }}>
          {emergencyVisits.map((visit, idx) => (
            <div
              key={visit.visit_id}
              style={{
                padding: '0.875rem',
                borderRadius: '0.75rem',
                marginTop: idx > 0 ? '0.625rem' : 0,
                border: `1px solid ${isDark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.15)'}`,
                background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(254,242,242,0.8)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>
                    {visit.patient_name || `${visit.first_name || ''} ${visit.last_name || ''}`.trim()}
                  </h4>
                  <PhoneButton phoneNumber={visit.patient_mobile} isDark={isDark} size="sm" />
                </div>
                <span style={{
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.5625rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: 'rgba(239,68,68,0.15)',
                  color: '#ef4444',
                  letterSpacing: '0.04em',
                }}>{visit.status}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <ProviderTag visit={visit} isDark={isDark} />
                {visit.queue_number && (
                  <span style={{
                    fontFamily: 'monospace',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    padding: '0.125rem 0.5rem',
                    borderRadius: '0.375rem',
                    background: isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6',
                    color: isDark ? '#e8e9ed' : '#374151',
                  }}>
                    #{visit.queue_number}
                  </span>
                )}
                {visit.visit_time && (
                  <span style={{ fontSize: '0.6875rem', color: isDark ? '#6b7280' : '#9ca3af', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    🕐 {formatVisitTime(visit.visit_time)}
                  </span>
                )}
              </div>
              {/* Quick-start if in QUEUE */}
              {visit.status === 'QUEUE' && (
                <button
                  onClick={() => onStart(visit.visit_id)}
                  disabled={isLoading}
                  style={{
                    marginTop: '0.625rem',
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                  }}
                >
                  ▶ Start Immediately
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Dismiss */}
        <div style={{ padding: '0 1.25rem 1.25rem' }}>
          <button
            onClick={onDismiss}
            style={{
              width: '100%',
              padding: '0.625rem',
              borderRadius: '0.75rem',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              background: 'transparent',
              color: isDark ? '#9ca3af' : '#6b7280',
              fontWeight: 500,
              fontSize: '0.8125rem',
              cursor: 'pointer',
            }}
          >
            Acknowledge & Continue
          </button>
        </div>
      </div>
    </div>
  );
}

